import {
  angleModeFor, visibleTargeted, SET_ANSWER_TRIGGER, displayEvaluation,
  haversineDistanceMeters,
  evaluateGPS,
  isGpsDefaultIncorrectUnreachable,
  isGpsFeedbackFullySubsumed,
  doesGpsFeedbackOverlapAccepted,
  findGpsOverlappingFeedbackPairs,
  sanitizeTriggerValue,
  checkBranchCoverage,
  branchesUnreachableInRange,
  enumerateFormulaValues,
  formulaBoundsCheap,
  formulaComboCount,
  evaluateEquation,
  resolveMatchedBranch, NO_SENSOR_DATA_TRIGGER,
  addGPSVersion, applyGPSVersion, removeGPSVersion, versionRollKey, writeGPSVersion,
  type GPSVersionOf, type SensorHealth, type GradingMode , type ConditionalLocationBranch, type FeedbackState, type GPSBlockData, type GPSCoordinateFormat, type GPSInterval, type GPSVisibilityMode , unreviewedCount , type DrivenFollower } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertTriangle,
  Crosshair,
  GripVertical,
  MapPin,
} from 'lucide-react';
import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useArmedDelete } from './block-actions';
import { type BlockIssue, type IssueCache, type IssueContext, IssuesPanel, memoizedByKey, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { BranchConditionCarousel } from './branch-condition-carousel';
import { requestBranchJump, useBranchJumpTarget } from './branch-jump';
import { formatCoverageList, minSampleValues } from './branch-setup';
import { BranchingCorrectsRow } from './branching-corrects-row';
import { EmptyQuestionNote, CollapsedInputBlock, CollapsedBlockBar, MobileOnlyChip, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { ConditionChipRow, toTestBarChips, type ConditionChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass } from './eval-chip';
import { FeedbackCard, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { gradingModes } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { fmtFixed, fmtLocale } from './number-format';
import { OptionsSection } from './options-section';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { SensorAdvisory } from './sensor-advisory';
import { SensorFeedbackCard } from './sensor-feedback-card';
import { SensorAnnounce, SensorCaptureButton, sensorReadoutTone } from './sensor-readout';
import { SensorSimulateBar, type SensorSourceMode, sensorHasReading, sensorSimRandom } from './sensor-simulate-bar';
import { accuracyForSignal, useSensorSimulator } from './sensor-simulator-context';
import { TOLERANCE_HINT_CLASS } from './tolerance-hint';
import { TriggerKindSelect, triggerKindOf } from './trigger-kind';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useGeolocationPermission } from './use-geolocation-permission';
import { useInputBlockFeedback, normalizeStandardFeedback, hasEmptyFeedbackContent, positiveTriggerForMode, retriggerPositiveSlot } from './use-input-block-feedback';
import { useRestoredData } from './use-restored-data';
import { useScenarioFollower } from './use-scenario-follower';
import { useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { ANSWER_COLUMN_CLASS, BlockQuestion, BlockPreviewProvider, htmlHasVariableChips, rollVariableValues, variableChipNames } from './variable-chip-preview';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { VersionSelector } from './version-selector';


import type { Block } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import Toast from '@/components/toast';
import { Button } from '@/components/ui/button';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { NumberField } from '@/components/ui/number-field';
import { RadioGroup } from '@/components/ui/radio-group';
import { SegmentedControl } from '@/components/ui/segmented-control';
import SelectMenu from '@/components/ui/select-menu';
import { SpokenPause } from '@/components/ui/spoken-pause';
import { SpokenPlaceholder } from '@/components/ui/spoken-placeholder';
import { TextField } from '@/components/ui/text-field';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { catchAllTitle, catchAllChips, CATCH_ALL_SOURCE, gpsChips, isGpsSetupIncomplete } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { COORD_DISPLAY_DECIMALS, gpsDistanceValue, gpsToleranceHint, gpsUseKm, gpsTargetedChipLabel, stateChipLabel } from '@/lib/block-state-labels';
import { effectiveSetting, settingAvailable } from '@/lib/mode-gated-settings';

// Whether this browser exposes the Geolocation API at all — a stable, one-time platform fact
// (not a media query), so computed once at module load rather than as a hook.
const GEO_SUPPORTED = typeof navigator !== 'undefined' && 'geolocation' in navigator;

// ─── Types ────────────────────────────────────────────────────────────────────

interface GPSBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped by Preview's page-level Submit — see the effect that watches it. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  /** Other GPS blocks' resolved targets in the same experience, for the cross-block distance
   * sanity check below — deliberately excludes this block's own id (see the filter at its use
   * site) so a block never flags itself. */
  siblingGpsTargets?: { blockId: string; taskTitle: string; lat: number; lng: number }[];
  blocks?: Block[];
  onRename?: (name: string) => void;
}

type GpsTriggerType = 'exact' | 'lt' | 'lte' | 'gt' | 'gte' | 'range';

// Editor-local shape — every numeric field is a string during editing (matches every other
// input block in this file set, e.g. slider's min/max/threshold), parsed only at validation/
// push time. Distinct name from @agavi/blocks' GPSBlockData (whose fields are already-parsed
// numbers) since this is what the teacher is actively typing, not what gets evaluated.
/** A branch as the EDITOR holds it: coordinates are strings, like every other numeric field in this
 * file, parsed only at validation/push time. `ConditionalLocationBranch` in @agavi/blocks is the
 * already-parsed counterpart. */
/** A scenario row as the EDITOR holds it — `ScenarioLocationBranch` with the same
 *  string-coordinate treatment `EditorLocationBranch` gets, and for the same reason. */
export interface EditorScenarioLocation {
  scenarioId: string;
  unreviewed?: boolean;
  targetLat: string;
  targetLng: string;
  feedback: { states: FeedbackState[] };
}

export interface EditorLocationBranch {
  id: string;
  operator: ConditionalLocationBranch['operator'];
  threshold: string;
  thresholdHigh?: string;
  targetLat: string;
  targetLng: string;
  /**
   * This branch's own feedback — see `ConditionalLocationBranch`, which gained it so GPS stops
   * being the one branching block of seven that could say only one thing about every target.
   *
   * OPTIONAL here where the package type has it required, and the difference is real: a branch
   * saved before the field existed has no such key, and the editor reads stored data. Absent
   * means "use the block's", which is what every GPS branch did until now.
   */
  feedback?: ConditionalLocationBranch['feedback'];
}

interface GpsBlockData extends DrivenFollower {
  /** Per-field angle modes, keyed by `fieldId` — see `angleModeFor`. */
  angleModes?: Record<string, 'degrees' | 'radians'>;
  /** This block's question bank — see `versions.ts`. A version carries the TARGET and how close
   *  counts; the coordinate format, visibility mode and accuracy cutoff stay shared (§3.2). */
  versions?: GPSVersionOf<string>[];
  question: string;
  targetLat: string;
  targetLng: string;
  /** The one shared expression every branch compares against. */
  conditionalEquation?: string;
  /** Alternative targets. Absent/empty means branching is off and targetLat/targetLng are the
   * answer. Tolerance and site conditions are NOT here: they describe how precisely the task must
   * be answered rather than where, so they stay on the block. */
  conditionalBranches?: EditorLocationBranch[];
  /** Following a scenario: the leader, and one target per variant. Mutually exclusive with
   *  `versions` (§2.2). Coordinates are strings here for the same reason the branch's are. */
  followsBlockId?: string;
  scenarioBranches?: EditorScenarioLocation[];
  toleranceRadius: string;
  /** Show the student how close they must get. Precision, never the target itself — see the same
   *  field on Numerical for the rule this follows. */
  showTolerance: boolean;
  accuracyCutoff: string;
  /** Not a string like the numeric fields above — it's a fixed three-way choice, never typed. */
  visibilityMode: GPSVisibilityMode;
  coordinateFormat: GPSCoordinateFormat;
  gradingMode: GradingMode;
  feedback: { states: FeedbackState[] };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// `navigator.geolocation` reports coordinates at full float precision — commonly 15+ decimal
// places, which renders as an unreadable wall of digits in the target fields and implies a
// precision the hardware does not have. 6 decimals is ~11cm at the equator, far finer than any
// consumer GPS fix (metres at best) and far finer than any classroom tolerance radius, so this
// truncates noise only. Applied at capture time so the staged reading, the committed target and
// the persisted value all agree — rounding only at display time would leave the stored number
// disagreeing with what the teacher was shown.
const COORD_DECIMALS = 6;

// Hard caps on what a teacher can type. Without them the tolerance field accepted any magnitude
// at all (a radius larger than the planet, which silently makes every possible answer correct),
// and the coordinate fields accepted unbounded digit strings. 20 km is deliberately generous —
// far beyond any field-work radius — while still being a number, not a universe.
const MAX_TOLERANCE_METERS = 20000;
// Enough for a signed 3-digit degree plus a decimal point and COORD_DECIMALS places.
const COORD_MAX_CHARS = 11;
// A tolerance is metres, always positive: 5 digits covers MAX_TOLERANCE_METERS, plus one decimal.
const TOLERANCE_MAX_CHARS = 7;
function roundCoord(v: number): number {
  return Number(v.toFixed(COORD_DECIMALS));
}

// Display precision is COORD_DISPLAY_DECIMALS (5, ~1.1 m), imported from block-state-labels so the
// readout, the answer bar and the derived chips/titles cannot disagree about how a coordinate is
// written. Deliberately coarser than COORD_DECIMALS above, which is what's STORED and evaluated
// against (6, ~11 cm) — display should never claim precision the hardware doesn't have.
function fmtCoord(v: number, locale: string): string {
  return fmtFixed(v, locale, COORD_DISPLAY_DECIMALS);
}

// Accuracy cutoff is a fixed set of presets, not free-text. GPS accuracy genuinely varies by
// deployment site (open sky vs. tree cover vs. dense canopy/urban canyon, commonly ~5m to
// 50m+), so unlike Compass's own accuracy gates — baked to one constant, since heading
// accuracy is mostly a device-quality signal, not a site-quality one — GPS can't collapse to a
// single number. But a free-text meter figure is exactly the "teacher types something absurd
// and silently blocks every student" hazard that motivated removing Compass's own free-text
// accuracy fields, and GPS has no manual-bypass safety valve at all (a deliberate, separate
// design choice — see the block's own scoping notes) to fall back on if that happens. Three
// labeled real-world conditions replace the opaque number instead.
const GPS_ACCURACY_PRESETS = [
  { id: 'open', meters: 8 },
  { id: 'partial', meters: 20 },
  { id: 'dense', meters: 50 },
] as const;
type GpsAccuracyPreset = typeof GPS_ACCURACY_PRESETS[number]['id'];
const ACCURACY_PRESET_I18N_KEYS: Record<GpsAccuracyPreset, string> = {
  open: 'accuracyPresetOpen', partial: 'accuracyPresetPartial', dense: 'accuracyPresetDense',
};

// Snaps an arbitrary stored number (e.g. a value saved before presets existed, or a custom
// figure a teacher typed in the old free-text field) to whichever preset is numerically
// closest, so an old block always resolves to a real dropdown selection rather than none.
function snapToNearestPresetMeters(meters: number): number {
  return GPS_ACCURACY_PRESETS.reduce<number>(
    (best, p) => (Math.abs(p.meters - meters) < Math.abs(best - meters) ? p.meters : best),
    GPS_ACCURACY_PRESETS[0].meters,
  );
}

function initData(raw: Record<string, unknown>): GpsBlockData {
  return {
    /* Builds a NEW object field by field, so anything not named is dropped — which is how a bank
       comes to be created, written to Mongo and then silently gone. */
    versions: raw.versions as GPSVersionOf<string>[] | undefined,
    question: (raw.question as string) ?? '',
    // Blank, not defaulted to '0' — an un-set target defaulting to (0, 0) would silently read
    // as "configured" (a real point in the Gulf of Guinea) rather than "not set yet", which is
    // exactly the setup-incomplete state this editor needs to detect and warn about.
    targetLat: raw.targetLat != null && raw.targetLat !== '' ? String(raw.targetLat) : '',
    targetLng: raw.targetLng != null && raw.targetLng !== '' ? String(raw.targetLng) : '',
    conditionalEquation: typeof raw.conditionalEquation === 'string' ? raw.conditionalEquation : undefined,
    angleModes: raw.angleModes as Record<string, 'degrees' | 'radians'> | undefined,
    conditionalBranches: Array.isArray(raw.conditionalBranches)
      ? (raw.conditionalBranches as EditorLocationBranch[]).map(b => ({
          ...b,
          targetLat: b.targetLat != null ? String(b.targetLat) : '',
          targetLng: b.targetLng != null ? String(b.targetLng) : '',
        }))
      : undefined,
    followsBlockId: raw.followsBlockId as string | undefined,
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as string | undefined,
    /* Stringified on the way in, exactly as the branches above are — a row and a branch differ
       in what selects them, never in what they hold, and that has to stay true of the editor's
       copy too or one of them is typed into and the other is not. */
    scenarioBranches: Array.isArray(raw.scenarioBranches)
      ? (raw.scenarioBranches as EditorScenarioLocation[]).map(r => ({
          ...r,
          targetLat: r.targetLat != null ? String(r.targetLat) : '',
          targetLng: r.targetLng != null ? String(r.targetLng) : '',
        }))
      : undefined,
    // 10m tolerance / 20m accuracy cutoff are usable, realistic starting points for a brand-new
    // block (key genuinely absent) — but if a teacher has explicitly cleared the field, `raw`
    // holds '' and that must be preserved as-is, not silently re-defaulted back to '10'/'20',
    // or isGpsSetupIncomplete() below would never see the blank state and the "GPS setup
    // incomplete" warning would never surface on the collapsed display.
    toleranceRadius: raw.toleranceRadius != null ? String(raw.toleranceRadius) : '10',
    showTolerance: (raw.showTolerance as boolean) ?? false,
    // Always snapped to one of the three presets — even for a block saved before presets
    // existed, or with a custom value from the old free-text field — so the dropdown always
    // shows a real selection. Absent entirely (brand-new block) snaps 20's own default to
    // 'partial', unchanged from the old hardcoded default.
    accuracyCutoff: String(snapToNearestPresetMeters(raw.accuracyCutoff != null ? Number(raw.accuracyCutoff) : 20)),
    // 'capture' is the default, not 'live': a GPS fix takes seconds to settle and drifts while it
    // does, so a continuously-updating coordinate readout invites a student to submit mid-drift.
    // Compass defaults to 'live' for the opposite reason — a magnetometer reading is instantaneous.
    // Blocks saved before this setting existed had exactly capture behaviour, so they land correctly.
    // LIVE by default, like Compass. Both are instruments a student reads off the screen; the
    // two sensors defaulting differently was the only reason one of them evaluated on sight
    // and the other did not.
    visibilityMode: (['live', 'capture', 'passive'].includes(raw.visibilityMode as string) ? raw.visibilityMode : 'live') as GPSVisibilityMode,
    // 'decimal' is the default because it's what the readout did before the setting existed, so no
    // saved block changes appearance, and because it matches what the teacher typed into the
    // Latitude/Longitude fields directly above it.
    coordinateFormat: (['decimal', 'cardinal'].includes(raw.coordinateFormat as string) ? raw.coordinateFormat : 'decimal') as GPSCoordinateFormat,
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string) ? raw.gradingMode : 'graded') as GradingMode,
    feedback: (raw.feedback ?? { states: [] }) as GpsBlockData['feedback'],
  };
}

// Same colon-delimited trigger format gps.ts's parseValueTrigger expects ("lt:20", "range:5:15",
// etc.), applied to a non-negative distance value rather than a signed one.
function parseGpsTrigger(trigger: string): { type: GpsTriggerType; v1: string; v2: string } {
  const parts = trigger.split(':');
  const raw = parts[0] ?? 'lt';
  const type = (['exact', 'lt', 'lte', 'gt', 'gte', 'range'].includes(raw) ? raw : 'lt') as GpsTriggerType;
  return { type, v1: parts[1] ?? '', v2: parts[2] ?? '' };
}
// Values are sanitised, not trusted: a trigger is ONE delimited string, so a colon inside a value
// silently re-splits it into a different trigger. See sanitizeTriggerValue.
function buildGpsTrigger(type: GpsTriggerType, v1: string, v2: string): string {
  const a = sanitizeTriggerValue(v1), b = sanitizeTriggerValue(v2);
  return type === 'range' ? `range:${a}:${b}` : `${type}:${a}`;
}

const TRIGGER_TYPES: { id: GpsTriggerType; symbol: string }[] = [
  { id: 'exact', symbol: '=' }, { id: 'lt', symbol: '<' }, { id: 'lte', symbol: '≤' },
  { id: 'gt', symbol: '>' }, { id: 'gte', symbol: '≥' }, { id: 'range', symbol: '↔' },
];

const TRIGGER_ARIA_KEYS: Record<GpsTriggerType, string> = {
  exact: 'triggerTypeExact', lt: 'triggerTypeLt', lte: 'triggerTypeLte',
  gt: 'triggerTypeGt', gte: 'triggerTypeGte', range: 'triggerTypeRange',
};

// Each of the six renders its own sentence. lt/lte shared one and gt/gte shared another, per the
// original plan's examples — but the control above offers all six as separate choices, so `<` and
// `≤` produced the identical chip with nothing to tell a teacher which they had picked. See
// `gpsTriggerSummary`; the same blindness is what left a catch-all unable to say whether it owned
// its own boundary.
/** Re-derives every targeted title after the TARGET changes. GPS titles embed the coordinates
 * (that's what makes Flow's chip match the panels'), and a title is a stored snapshot — so moving
 * the target would otherwise leave "Within 60 m from <old coords>" behind forever. Same job
 * resyncKeywordTargetedTitles does when a keyword is renamed; GPS needs it now for the same reason
 * those blocks always did. */
function resyncGpsTargetedTitles(states: FeedbackState[], d: GpsBlockData, t: (k: string, opts?: Record<string, unknown>) => string, locale: string): FeedbackState[] {
  return states.map(s => s.type === 'targeted' ? { ...s, title: gpsTargetedTitle(s.trigger, d, t, locale) } : s);
}

// Identical to the chip label by contract (see block-state-labels.ts) — Flow renders this stored
// title while the panels derive live, so they can only agree if the two are the same string.
function gpsTargetedTitle(trigger: string, d: GpsBlockData, t: (k: string, opts?: Record<string, unknown>) => string, locale: string): string {
  return gpsTargetedChipLabel(trigger, d, t, locale);
}

function isValidCoord(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

type ParsedPaste = { coord: { lat: number; lng: number } } | { errorKey: string };

// Accepts raw "lat, lng" text and three Google Maps URL shapes. The pin form (!3d<lat>!4d<lng>)
// is checked before the @lat,lng viewport form on purpose — a Maps URL for a dropped pin can
// carry both (the map's current viewport center *and* the actual pin coordinates), and the pin
// is the point the teacher meant to share, not wherever the map happened to be centered.
// Shortened links (goo.gl, maps.app.goo.gl) never contain the coordinates in the URL text
// itself (they resolve server-side), so they're rejected outright with guidance to paste the
// expanded link or raw coordinates instead.
function parsePastedLocation(raw: string): ParsedPaste {
  const s = raw.trim();
  if (!s) return { errorKey: 'pasteErrorEmpty' };
  if (/goo\.gl/i.test(s)) return { errorKey: 'pasteErrorShortenedLink' };

  const pinMatch = s.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (pinMatch) {
    const lat = parseFloat(pinMatch[1]!), lng = parseFloat(pinMatch[2]!);
    return isValidCoord(lat, lng) ? { coord: { lat, lng } } : { errorKey: 'pasteErrorOutOfRange' };
  }

  const viewportMatch = s.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),/);
  if (viewportMatch) {
    const lat = parseFloat(viewportMatch[1]!), lng = parseFloat(viewportMatch[2]!);
    return isValidCoord(lat, lng) ? { coord: { lat, lng } } : { errorKey: 'pasteErrorOutOfRange' };
  }

  const qMatch = s.match(/[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
  if (qMatch) {
    const lat = parseFloat(qMatch[1]!), lng = parseFloat(qMatch[2]!);
    return isValidCoord(lat, lng) ? { coord: { lat, lng } } : { errorKey: 'pasteErrorOutOfRange' };
  }

  const rawMatch = s.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
  if (rawMatch) {
    const lat = parseFloat(rawMatch[1]!), lng = parseFloat(rawMatch[2]!);
    return isValidCoord(lat, lng) ? { coord: { lat, lng } } : { errorKey: 'pasteErrorOutOfRange' };
  }

  return { errorKey: 'pasteErrorUnrecognized' };
}

// A block counts as "not set up yet" if its target has no valid coordinate or the tolerance
// radius is missing/non-positive — mirrors isAnswerSetupIncomplete's role in slider-block.tsx.
// accuracyCutoff has no equivalent check anymore: it's a dropdown constrained to one of the
// three presets, so it can never be missing or malformed.

/** Moved to `lib/block-condition-chips` — see that file's note. */

// Other GPS blocks in this experience whose target is implausibly far (>50km) from this one's —
// shared by the issue computation and the editor's own inline list so the two can't diverge.
// A block never flags itself, and an unconfigured target flags nothing.
function gpsSiblingWarnings(
  lat: number,
  lng: number,
  blockId: string,
  siblingTargets: { blockId: string; taskTitle: string; lat: number; lng: number }[] | undefined,
): { taskTitle: string; distanceKm: number }[] {
  if (!isValidCoord(lat, lng)) return [];
  return (siblingTargets ?? [])
    .filter(s => s.blockId !== blockId)
    .map(s => ({ ...s, distanceKm: haversineDistanceMeters({ lat, lng }, { lat: s.lat, lng: s.lng }) / 1000 }))
    .filter(s => s.distanceKm > 50);
}

// ─── Standardized issue reporting ──────────────────────────────────────────────
// Mirrors orderingIssues/mcqIssues' shape exactly — one computation read by the collapsed
// card's own top-priority bar and the in-editor sticky warningBarSlot, so the two surfaces can
// never disagree about wording. Replaces GPS's own bespoke warning-only bar, which had no error
// tier at all and rendered every validation failure as inline text next to its field; per the
// 2026-08-02 standing rule, a persistent setup defect is a highlighted field plus a message on
// the issue bar, never inline.
//
// Deliberately NOT routed here (they are not persistent setup defects, so they'd be wrong on a
// bar that eventually gates publication): a failed capture attempt and an unparseable pasted
// coordinate are one-off action failures shown as Toasts, and the pending-capture accuracy /
// network-fix notes stay inline because they guide a still-open decision ("accept this staged
// reading?") and vanish the moment it's accepted or discarded.
/** Backfills the default feedback set for a block read straight from `block.data` — the editor
 * seeds these into its own state, but nothing reaches the saved block until the teacher changes
 * something, so without this a freshly added block's test legend has no states to show while the
 * feedback panel beside it lists several. Shared by both non-editing render paths (the collapsed
 * card's test widget and the student-facing Preview). */
function buildNormalizedGpsData(
  rawD: GpsBlockData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, or the one the teacher's pager is on. */
  versionId?: string,
): GpsBlockData {
  const rawVersioned = applyGPSVersion(rawD, versionId);
  return { ...rawVersioned, feedback: normalizeStandardFeedback(rawVersioned.feedback, t, rawVersioned.gradingMode, {
    defaults: { unansweredContent: `<p>${t('blocks.feedbackDefaults.noMeasurementContent')}</p>` },
    extraDefaults: [{
      // Ordered as the evaluator gates them: no reading at all, then a reading too poor to grade.
      trigger: NO_SENSOR_DATA_TRIGGER,
      title: t('blocks.sensors.noSensorDataFeedback'),
      content: `<p>${t('blocks.gps.noSensorDataDefault')}</p>`,
      evaluation: 'neutral',
    }, {
      trigger: 'poor_accuracy',
      title: t('blocks.gps.poorAccuracyFeedback'),
      content: `<p>${t('blocks.gps.poorAccuracyDefault')}</p>`,
      evaluation: 'neutral',
    }],
  }) };
}

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `gpsIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function gpsBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return gpsIssues(buildNormalizedGpsData(initData(block.data), ctx.t), ctx);
}

export function gpsIssues(d: GpsBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables, locale, blockId, siblingGpsTargets: siblingTargets } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryFeedback = t('blocks.areas.feedback');

  const lat = parseFloat(d.targetLat), lng = parseFloat(d.targetLng);
  // Branch coverage, reusing the shared engine unchanged. A gap is an ERROR: the fallback target is
  // 0,0, so a student whose equation lands in the gap can never be correct — which is deliberate
  // (never silence), but it is not something to ship.
  const gpsBranches = d.conditionalBranches ?? [];
  if (d.gradingMode === 'graded' && gpsBranches.length > 0) {
    // The selector itself, which GPS never reported on. Without these two the equation could be
    // blank or unparseable and the only symptom was a coverage gap over a domain that did not
    // exist — a confusing way to be told the expression is missing. Same keys, severities and
    // kinds as the six content blocks: absence is an incompletion, garbage is a defect.
    const gpsEquation = d.conditionalEquation?.trim();
    if (!gpsEquation) {
  /**
   * §4.4 — a row copied by the projection is "copied, not decided". The box shows a count
   * beside the pager for the row you are on; this is the half that reaches the publication
   * gate, so a teacher who never pages to scenario 5 still learns it was never reviewed.
   */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
      issues.push({ key: 'no-equation', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup,
        detail: t('blocks.branching.diagnosticNoEquation') });
    } else if (evaluateEquation(gpsEquation, minSampleValues(variables), angleModeFor(d, 'conditionalEquation')) === null) {
      issues.push({ key: 'invalid-equation', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
        detail: t('blocks.branching.equationInvalidWarning') });
    }
    const gpsEq = (d.conditionalEquation ?? '').trim();
    const domainValues = enumerateFormulaValues(gpsEq, variables);
    // Too many values to build the set, but still a discrete variable. The real-line gap analysis
    // would then name regions it can never produce ("<0, >0" for a 1-to-100 variable), so gaps are
    // suppressed and the teacher is told coverage went unchecked.
    const comboCount = domainValues ? null : formulaComboCount(gpsEq, variables);
    const discreteButUnchecked = comboCount !== null;
    const coverage = checkBranchCoverage(
      gpsBranches.map(b => ({ id: b.id, operator: b.operator, threshold: b.threshold, thresholdHigh: b.thresholdHigh })),
      domainValues,
    );
    const gapIssues = coverage.issues.filter(i => i.matchingBranchIds.length === 0);
    const overlapIssues = coverage.issues.filter(i => i.matchingBranchIds.length > 1);
    if (gapIssues.length > 0 && !discreteButUnchecked) {
      issues.push({ key: 'coverage-gap', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
        detail: t('blocks.branching.missingCoverage', { list: formatCoverageList(gapIssues, t, 'gap') }) });
    }
    if (overlapIssues.length > 0 && !discreteButUnchecked) {
      issues.push({ key: 'coverage-overlap', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
        detail: t('blocks.branching.overlappingConditions', { list: formatCoverageList(overlapIssues, t, 'overlap') }) });
    }
    if (discreteButUnchecked) {
      issues.push({ key: 'coverage-unchecked', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
        detail: t('blocks.branching.coverageUnchecked', { count: comboCount }) });
    }
    // Reachability survives a domain too large to enumerate: bounds alone prove a threshold
    // outside them can never be met, and for a bare variable the bounds are its own min and max.
    const cheapBounds = domainValues ? null : formulaBoundsCheap(gpsEq, variables, angleModeFor(d, 'conditionalEquation'));
    if (cheapBounds) {
      branchesUnreachableInRange(gpsBranches, cheapBounds.lo, cheapBounds.hi).forEach(id => {
        const number = gpsBranches.findIndex(b2 => b2.id === id) + 1;
        if (number === 0) return;
        issues.push({ key: `branch-${id}-unreachable`, severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
          detail: t('blocks.branching.branchUnreachable', { number, lo: cheapBounds.lo, hi: cheapBounds.hi }) });
      });
    }
    // A condition no attainable value can satisfy: its whole answer setup is dead. A WARNING, not
    // an error — nothing a student does can reach it, so it cannot mis-grade anyone, and where it
    // does cause harm the coverage gap beside it already says so at error severity. Matches
    // `feedback-overlaps-correct`, the existing "authored but can never fire" issue.
    if (domainValues && domainValues.length > 0) {
      coverage.unreachableBranchIds.forEach(id => {
        const number = (gpsBranches ?? []).findIndex(b2 => b2.id === id) + 1;
        if (number === 0) return;
        issues.push({ key: `branch-${id}-unreachable`, severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
          detail: t('blocks.branching.branchUnreachable', { number, lo: domainValues[0], hi: domainValues[domainValues.length - 1] }) });
      });
    }
    // A branch with no location is the branching equivalent of an unset target.
    if (gpsBranches.some(b => b.targetLat.trim() === '' || b.targetLng.trim() === '')) {
      issues.push({ key: 'branch-target-missing', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup,
        detail: t('blocks.gps.branchTargetMissing') });
    }
  }

  const tol = parseFloat(d.toleranceRadius);
  // Malformed-but-non-blank only, matching the field-level `latInvalid`/`lngInvalid`/
  // `toleranceInvalid` highlighting exactly — a blank field is covered by setup-incomplete
  // below, and flagging it twice would put two messages on the bar for one empty input.
  const latInvalid = d.targetLat.trim() !== '' && (!Number.isFinite(lat) || Math.abs(lat) > 90);
  const lngInvalid = d.targetLng.trim() !== '' && (!Number.isFinite(lng) || Math.abs(lng) > 180);
  const toleranceInvalid = d.toleranceRadius.trim() !== '' && (!Number.isFinite(tol) || tol <= 0);
  const toleranceTooLarge = Number.isFinite(tol) && tol > MAX_TOLERANCE_METERS;

  if (latInvalid) issues.push({ key: 'lat-range', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.gps.validationLatRange') });
  if (lngInvalid) issues.push({ key: 'lng-range', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.gps.validationLngRange') });
  if (toleranceInvalid) issues.push({ key: 'tolerance-positive', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.gps.validationTolerancePositive') });
  if (toleranceTooLarge) issues.push({ key: 'tolerance-too-large', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.gps.validationToleranceMax', { max: MAX_TOLERANCE_METERS }) });

  if (!d.question.replace(/<\/?p>/g, '').trim()) {
    issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  }

  // No usable target or radius means there's nothing to grade against at all. Suppressed while
  // a malformed value is already being reported above, so one bad keystroke yields one message.
  // Graded only: optional and survey author no answer key, so a missing one is the mode
  // working as intended rather than a setup the teacher has not finished.
  if (d.gradingMode === 'graded' && isGpsSetupIncomplete(d) && !latInvalid && !lngInvalid && !toleranceInvalid) {
    issues.push({ key: 'setup-incomplete', severity: 'error', kind: 'incompletion', supersededBy: ['lat-range', 'lng-range', 'tolerance-positive', 'tolerance-too-large'], category: categoryAnswerSetup, detail: t('blocks.gps.barSetupIncomplete') });
  }

  // A targeted trigger whose range reads high-to-low can never fire. Requires an explicit
  // authoring action to reach, so it isn't grace-gated.
  const invertedRange = d.feedback.states.some(s => {
    if (s.type !== 'targeted') return false;
    const { type: trigType, v1, v2 } = parseGpsTrigger(s.trigger);
    const lo = parseFloat(v1), hi = parseFloat(v2);
    return trigType === 'range' && v1 !== '' && v2 !== '' && Number.isFinite(lo) && Number.isFinite(hi) && lo > hi;
  });
  if (invertedRange) issues.push({ key: 'trigger-range-inverted', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.gps.validationRangeInverted') });

  // A targeted state whose distance is blank can never fire — `distance <= NaN` is always false —
  // so it silently does nothing while looking authored. For a range, EITHER bound being blank is
  // enough to break it. Not grace-gated: adding a targeted state is an explicit action.
  const blankValue = (v: string) => v.trim() === '' || !Number.isFinite(parseFloat(v));
  // The NAMED triggers carry no number by design — Answer reads the authored radius, No Response
  // fires on an empty reading. Parsing them as distances made both look like a blank the teacher
  // forgot to fill in.
  const targetedTriggers = d.feedback.states
    .filter(s => s.type === 'targeted' && s.trigger !== SET_ANSWER_TRIGGER)
    .map(s => parseGpsTrigger(s.trigger));
  const missingDistance = targetedTriggers.some(({ type: trigType, v1 }) => trigType !== 'range' && blankValue(v1));
  // A between-trigger with only one bound filled in is a different mistake from one with no number
  // at all: the teacher started authoring a range and stopped, rather than never entering anything.
  const incompleteRange = targetedTriggers.some(({ type: trigType, v1, v2 }) => trigType === 'range' && (blankValue(v1) || blankValue(v2)));
  if (missingDistance) issues.push({ key: 'trigger-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.gps.validationTriggerIncomplete') });
  if (incompleteRange) issues.push({ key: 'trigger-range-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.gps.validationRangeIncomplete') });

  if (hasEmptyFeedbackContent(d.feedback.states)) {
    issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });
  }

  // Feedback-vs-feedback and feedback-vs-Correct conflicts, aggregated one-per-kind rather than
  // one-per-pair (matching the MCQ/Dropdown chip-collision collapse). Only meaningful once the
  // accepted radius itself is usable, hence the graded + complete-setup gate.
  if (d.gradingMode === 'graded' && !isGpsSetupIncomplete(d)) {
    const acceptedRange: GPSInterval | null = Number.isFinite(tol) && tol > 0 ? { lo: 0, hi: tol } : null;
    const triggers = d.feedback.states.filter(s => s.type === 'targeted').map(s => s.trigger);
    if (findGpsOverlappingFeedbackPairs(triggers).length > 0) {
      issues.push({ key: 'feedback-overlap', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.gps.feedbackOverlapWarning') });
    }
    if (triggers.some(trig => isGpsFeedbackFullySubsumed(trig, acceptedRange))) {
      issues.push({ key: 'feedback-subsumed', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.gps.feedbackSubsumedWarning') });
    }
    if (triggers.some(trig => !isGpsFeedbackFullySubsumed(trig, acceptedRange) && doesGpsFeedbackOverlapAccepted(trig, acceptedRange))) {
      issues.push({ key: 'feedback-overlaps-correct', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.gps.feedbackOverlapsCorrectWarning') });
    }
  }

  // Target sits implausibly far from another GPS block's target in the same experience — a
  // strong hint that one of them was mistyped or captured at the wrong site.
  for (const w of gpsSiblingWarnings(lat, lng, blockId, siblingTargets)) {
    issues.push({
      key: `cross-block-${w.taskTitle}`,
      severity: 'warning',
      kind: 'defect',
      category: categoryAnswerSetup,
      detail: t('blocks.gps.crossBlockWarning', { taskTitle: w.taskTitle, distance: fmtLocale(w.distanceKm, locale) }),
    });
  }

  return issues;
}

// Module-level (not per-instance) — same reasoning as mcq/ordering's own caches: without it this
// recomputes on every keystroke anywhere in the task, not just in this block's own fields.
const gpsIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI ───────────────────────────────────────────────────────────────

function GpsTriggerEditor({ trigger, onChange, t, onPlainFieldFocus, gradingMode, answerConditionChips, answerTaken }: {
  trigger: string; onChange: (t: string) => void; t: (k: string, opts?: Record<string, unknown>) => string;
  onPlainFieldFocus: () => void;
  /** Another card already points at the answer — see `TriggerKindSelect`. */
  answerTaken: boolean;
  gradingMode: GradingMode;
    /** The authored condition, rendered by the parent — it owns the data and the roll. */
  answerConditionChips: React.ReactNode;
}) {
  const { type: trigType, v1, v2 } = parseGpsTrigger(trigger);
  const kind = triggerKindOf(trigger);
  const loNum = parseFloat(v1), hiNum = parseFloat(v2);
  const rangeInverted = trigType === 'range' && v1 !== '' && v2 !== ''
    && Number.isFinite(loNum) && Number.isFinite(hiNum) && loNum > hiNum;

  return (
    <div className='flex flex-col gap-2'>
      <TriggerKindSelect
        trigger={trigger}
        gradingMode={gradingMode}
        answerTaken={answerTaken}
        onChange={onChange}
        customDefault={buildGpsTrigger('exact', '', '')}
      />
      {/* The PRESET shows the authored condition in the same place a custom one appears, read
          only — the teacher sees what it will match without being able to edit it here, and
          editing the answer upstream moves it. */}
      {kind === 'answer' ? (
        <div className='pointer-events-none opacity-60'>{answerConditionChips}</div>
      ) : (
      <>
      <div role='group' aria-label={t('blocks.gps.conditionTypeLabel')} className='flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5'>
        {TRIGGER_TYPES.map(({ id, symbol }) => (
          <button
            key={id}
            type='button'
            aria-pressed={trigType === id}
            aria-label={t(`blocks.gps.${TRIGGER_ARIA_KEYS[id]}`)}
            onClick={() => onChange(buildGpsTrigger(id, v1, v2))}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
              trigType === id ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted hover:text-ws-text'
            }`}
          >
            {symbol}
          </button>
        ))}
      </div>
      {trigType === 'range' ? (
        <>
          {/* STACKED, matching slider's own trigger pair. Two bounds sharing one row inside an
              already-narrow feedback card left each about half a field wide. */}
          <div className='flex flex-col gap-1.5'>
            {/* pr-6 rather than Compass's and Angle's pr-5: "m" is a wider glyph than "°". */}
            {/* `decimal` throughout this block, not `scientific`: a coordinate runs -180..180
                and a radius is metres a teacher types as 10000, so an exponent has no use here.
                Numerical and Slider are `scientific` because their domains are unbounded. The
                split is per-domain and deliberate, not an oversight in either direction. */}
            <NumberField
                grammar='decimal'
                value={v1}
                onChange={v => onChange(buildGpsTrigger('range', v, v2))}
                hasError={rangeInverted}
                suffix={t('blocks.gps.unitMeters')}
                ariaLabel={t('blocks.gps.triggerRangeMinAria')}
                placeholder={t('blocks.triggers.triggerRangeMin')}
                onFocus={onPlainFieldFocus}
                className='w-full' />
            <span aria-hidden='true' className='self-center text-xs text-ws-muted'>{t('blocks.connectors.to')}</span>
            <NumberField
                grammar='decimal'
                value={v2}
                onChange={v => onChange(buildGpsTrigger('range', v1, v))}
                hasError={rangeInverted}
                suffix={t('blocks.gps.unitMeters')}
                ariaLabel={t('blocks.gps.triggerRangeMaxAria')}
                placeholder={t('blocks.triggers.triggerRangeMax')}
                onFocus={onPlainFieldFocus}
                className='w-full' />
          </div>
        </>
      ) : (
        // The unit was only implied by the summary chip before, so the field itself gave no hint
        // that this threshold is in metres. A JS comment, not a JSX one: a ternary branch is an
        // expression position, where {/* */} is a syntax error rather than a comment.
        <NumberField
          grammar='decimal'
          value={v1}
          onChange={v => onChange(buildGpsTrigger(trigType, v, ''))}
          suffix={t('blocks.gps.unitMeters')}
          ariaLabel={t('blocks.gps.triggerValuePlaceholder')}
          placeholder={t('blocks.gps.triggerValuePlaceholder')}
          onFocus={onPlainFieldFocus}
          className='w-full' />
      )}
      </>
      )}
    </div>
  );
}

function GpsTriggerChip({ trigger, evaluation, t, d, locale }: {
  trigger: string; evaluation: FeedbackState['evaluation']; t: (k: string, opts?: Record<string, unknown>) => string;
  d: GpsBlockData; locale: string;
}) {
  const chipColor =
    chipToneClass(evaluation);
  return (
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      <span className={TRIGGER_CHIP_TEXT_CLASS}>{gpsTargetedChipLabel(trigger, d, t, locale)}</span>
    </span>
  );
}

// ─── Collapsed-view display ────────────────────────────────────────────────────
// No map, no track widget — GPS's collapsed answer bar is plain text/chips only, per the
// approved plan ("resolved-coordinate + accuracy plain-text display, NO map anywhere").

// ─── Live test: scenario-driven simulated readings ─────────────────────────────
// See compass-block.tsx's own header comment for the reasoning. GPS's twist is that a reading is
// a coordinate, not a scalar, so each scenario is expressed as "a point this many metres from the
// target" and converted to a real lat/lng — which keeps the haversine distance maths in
// evaluateGPS genuinely exercised rather than short-circuited.
type GpsScenarioId = 'exact' | 'justInside' | 'justOutside' | 'wrongLocation' | 'poorAccuracy' | 'networkFix' | 'noReading';

// 'noReading' is last because it's the odd one out — it deliberately produces NO reading, which is
// the only way to reach the No Answer state now that two of the three visibility modes have no
// capture button to withhold (Live Reading always has a value; Capture on Submit takes one
// automatically). It's a real field case too: denied permission, or no fix indoors.
const GPS_SCENARIO_IDS: GpsScenarioId[] = ['exact', 'justInside', 'justOutside', 'wrongLocation', 'poorAccuracy', 'networkFix', 'noReading'];
/**
 * The scenarios that describe the RECEIVER rather than a position relative to the target.
 *
 * The first four are measured from the target and need one; these three do not, so they stay
 * offered on a block with no answer — which in Optional is every block, and while roughing out a
 * graded one is most of them. The whole bar used to be disabled instead, so a teacher who wanted
 * to watch their block behave had a dead dropdown and a readout stuck on zero.
 */
const GPS_DEVICE_SCENARIO_IDS: GpsScenarioId[] = ['poorAccuracy', 'networkFix', 'noReading'];

/** What the simulated sensor is currently armed to report. A named scenario is relative to the
 * authored target (so changing the target changes what every scenario means, which is the point),
 * while a manual coordinate is absolute — the escape hatch for a specific point the teacher has in
 * mind that no named scenario expresses. */
type GpsSimSelection =
  | { kind: 'scenario'; id: GpsScenarioId }
  | { kind: 'manual'; lat: number; lng: number }
  /**
   * "Specific Location" chosen with nothing typed yet — the block's default, and the state a teacher
   * lands in before they have decided anything.
   *
   * Needed as its own variant because `manual` cannot represent it: that one carries real
   * coordinates, and there is no pair of numbers meaning "none". Without it the dropdown's empty
   * option built `{ kind: 'scenario', id: '' }` and simulated a preset that does not exist.
   */
  | { kind: 'none' };

interface GpsSimReading {
  lat: number;
  lng: number;
  accuracy: number;
  /** Metres from the target — shown in the Simulate panel so the teacher can see what the
   * scenario actually means for their current tolerance without doing the arithmetic.
   *
   * `null` when there is no target to measure from, which only a manually typed point can reach:
   * every preset is constructed as an offset from the target and so cannot exist without one. The
   * point itself is still a perfectly good reading and still drives the readout — it is only the
   * distance that is unknowable, so this is null rather than 0, which would read as "on target". */
  distanceMeters: number | null;
  /** Drives the same network-fix hint a real staged capture shows. */
  looksNetworkBased: boolean;
}

/** Metres of latitude per degree — the offset direction is deliberately due north so the
 * conversion needs no longitude/cos(lat) correction and stays exact at any latitude. */
const METERS_PER_DEG_LAT = 111320;

const GPS_GOOD_ACCURACY = 4; // metres — a healthy open-sky fix

function gpsSimReading(selection: GpsSimSelection, d: GpsBlockData, nonce: number): GpsSimReading | null {
  const lat = parseFloat(d.targetLat), lng = parseFloat(d.targetLng);
  const tolerance = parseFloat(d.toleranceRadius);
  const cutoff = parseFloat(d.accuracyCutoff);

  if (selection.kind === 'none') return null;

  // A manually entered point is taken at face value, so its distance is measured rather than
  // constructed — the same haversine evaluateGPS itself will run, so the metres shown in the hint
  // and the metres the evaluation scores against cannot disagree.
  //
  // Resolved BEFORE the setup gate below, unlike every preset. A preset is an offset from the
  // target and needs both the target and a tolerance to exist; a typed point needs neither, so
  // gating it on them left the box dead on a half-authored block. With no valid target the point is
  // still shown — only the distance is unmeasurable, and it says so rather than claiming zero.
  if (selection.kind === 'manual') {
    const haveTarget = isValidCoord(lat, lng);
    return {
      lat: selection.lat,
      lng: selection.lng,
      accuracy: GPS_GOOD_ACCURACY,
      distanceMeters: haveTarget
        ? haversineDistanceMeters({ lat, lng }, { lat: selection.lat, lng: selection.lng })
        : null,
      looksNetworkBased: false,
    };
  }

  if (isGpsSetupIncomplete(d)) return null;
  if (!isValidCoord(lat, lng) || !(Number.isFinite(tolerance) && tolerance > 0)) return null;

  const rnd = (salt: number) => sensorSimRandom(nonce, salt);
  // A random bearing, not a fixed due-north offset. The old version only ever moved the latitude, so
  // longitude and the haversine's own cos(latitude) term were never exercised by any scenario — a bug
  // in either would have passed every simulated test.
  const bearing = rnd(1) * 2 * Math.PI;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const at = (distanceMeters: number, accuracy: number, looksNetworkBased = false): GpsSimReading => {
    const north = distanceMeters * Math.cos(bearing);
    const east = distanceMeters * Math.sin(bearing);
    // Near the poles cos(latitude) collapses and the east/west conversion blows up, so fall back to a
    // pure north/south offset there — the distance is still exactly right, only the direction is
    // constrained.
    const usableCosLat = Math.abs(cosLat) > 0.01;
    return {
      lat: roundCoord(lat + (usableCosLat ? north : distanceMeters) / METERS_PER_DEG_LAT),
      lng: roundCoord(usableCosLat ? lng + east / (METERS_PER_DEG_LAT * cosLat) : lng),
      accuracy,
      distanceMeters,
      looksNetworkBased,
    };
  };

  switch (selection.id) {
    // Exact stays exact — it's the one scenario whose whole point is the zero case.
    case 'exact':       return at(0, GPS_GOOD_ACCURACY);
    // Anywhere inside the radius, not always 90% of it: a rounding bug that only shows up close to
    // the centre, or close to the edge, needs both to be reachable.
    case 'justInside':  return at(tolerance * (0.1 + 0.85 * rnd(2)), GPS_GOOD_ACCURACY);
    // Just past the edge — near enough to still be a near miss, far enough to be unambiguous.
    case 'justOutside': return at(tolerance * (1.05 + 0.45 * rnd(3)), GPS_GOOD_ACCURACY);
    // Far enough to be unambiguous at any sane tolerance, and far enough to fall outside every
    // plausible targeted distance band too.
    case 'wrongLocation': return at(Math.max(tolerance * (30 + 40 * rnd(4)), 3000 + 4000 * rnd(5)), GPS_GOOD_ACCURACY);
    // Inside the radius but too rough to trust: proves the accuracy gate fires even on a position
    // that would otherwise score correct.
    case 'poorAccuracy':  return at(tolerance * 0.8 * rnd(6), (Number.isFinite(cutoff) ? cutoff : 0) + 5 + 45 * rnd(7));
    // A WiFi/IP-derived fix: self-reports a plausible accuracy while sitting far from the truth.
    // Distance is what makes it wrong; the flag is what a teacher would have been warned about.
    case 'networkFix':    return at(Math.max(tolerance * (10 + 20 * rnd(8)), 1000 + 2000 * rnd(9)), GPS_GOOD_ACCURACY * 3, true);
    // No fix at all — the block is answered-with-nothing, which is what reaches No Answer.
    case 'noReading':     return null;
  }
}

interface GpsTestData {
  /** Which branch the equation selected for THIS test, and where it sits in the list. -1/null when
   * branching is off or nothing matched. */
  matchedBranchIndex: number;
  testResult: FeedbackState[] | null;
  testChips: TestBarChip[];
  hasChips: boolean;
}

function gpsBlockDataForEval(d: GpsBlockData): GPSBlockData {
  return {
    question: d.question,
    targetLat: parseFloat(d.targetLat),
    targetLng: parseFloat(d.targetLng),
    toleranceRadius: parseFloat(d.toleranceRadius),
    accuracyCutoff: parseFloat(d.accuracyCutoff),
    visibilityMode: d.visibilityMode,
    coordinateFormat: d.coordinateFormat,
    gradingMode: d.gradingMode,
    feedback: d.feedback,
    conditionalEquation: d.conditionalEquation,
    // Editor branches hold string coordinates like every other numeric field here; the evaluator
    // wants numbers. A branch whose target is still blank parses to NaN, which can never be within
    // any tolerance — the same "never accidentally correct" property the top-level target has.
    conditionalBranches: d.conditionalBranches?.map(b => ({
      id: b.id, operator: b.operator, threshold: b.threshold, thresholdHigh: b.thresholdHigh,
      targetLat: parseFloat(b.targetLat), targetLng: parseFloat(b.targetLng),
      // Enumerated like the rest of this builder, so the field has to be named or it is
      // dropped. `undefined` on a branch that has none, which `activeFeedbackForBranch`
      // reads as "use the block's".
      feedback: b.feedback,
    })) as ConditionalLocationBranch[] | undefined,
  };
}

function computeGpsTestData(
  d: GpsBlockData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  reading: GpsSimReading | null,
  hasSubmitted: boolean,
  /** The roll the branch equation resolves against — the block's own while testing, the task-level
   * one in Preview. */
  resolvedVariables: Record<string, number> = {},
  /**
   * The device produced nothing. Passed rather than inferred from a null `reading`, because a null
   * reading is also what a student who has not captured yet has — and that is an ordinary No
   * Answer, not a hardware failure.
   */
  sensorDead = false,
): GpsTestData {
  const evalData = gpsBlockDataForEval(d);
  // GPS has no manual bypass at all, deliberately — so a dead receiver is never the student's
  // fault and `bypassAvailable` is never set here. This is the block that most needs the card.
  const health: SensorHealth = sensorDead
    ? { fault: 'noData' }
    : { positionAccuracy: reading?.accuracy ?? 0 };
  const branchList = d.gradingMode === 'graded' ? (d.conditionalBranches ?? []) : [];
  const matchedGpsBranch = branchList.length > 0
    ? resolveMatchedBranch((d.conditionalEquation ?? '').trim(), evalData.conditionalBranches ?? [], resolvedVariables, 'degrees')
    : null;
  const matchedBranchIndex = matchedGpsBranch ? branchList.findIndex(b => b.id === matchedGpsBranch.id) : -1;
  const testResult = !hasSubmitted ? null
    : evaluateGPS(
        reading
          ? { touched: true, lat: reading.lat, lng: reading.lng }
          : { touched: false, lat: 0, lng: 0 },
        // `accuracy` used to ride inside the answer. It is the receiver's report, not the
        // student's, so it travels beside it now — see SensorHealth.
        health,
        evalData,
        resolvedVariables,
      );
  const firedIds = new Set((testResult ?? []).map(s => s.id));
  const states = d.feedback.states;
  const find = (trigger: string) => states.find(s => s.type === 'default' && s.trigger === trigger);

  const chips: TestBarChip[] = [];
  const push = (s: FeedbackState | undefined, category: TestBarChip['category'], condition: React.ReactNode, reachable?: boolean) => {
    if (!s) return;
    chips.push({ id: s.id, condition, evaluation: s.evaluation, active: firedIds.has(s.id), category, reachable });
  };

  /**
   * The participation arms used to emit the catch-all and RETURN, which put the early exit above
   * the targeted loop below — fine while a value block could not fire a targeted state outside
   * graded, wrong the moment it could. The mode now decides what to SKIP, not where to stop.
   */
  const participation = d.gradingMode !== 'graded';

  // Gate chips carry their own condition text (No Answer / the accuracy cutoff) — passing null
  // here rendered them as an icon with no label beside the category heading.
  const gateLabel = (trigger: string) => stateChipLabel({ id: '', type: 'gps', data: d } as never, { type: 'default', trigger }, t, locale);
  if (d.gradingMode !== 'optional') {
    push(find('unanswered'), 'unanswered', gateLabel('unanswered'));
  }
  // The accuracy gate sits between "answered" and "graded" in evaluateGPS, so it's a real state.
  push(find('poor_accuracy'), 'incomplete', gateLabel('poor_accuracy'));

  // Correct needs a target coordinate as well as a usable radius — a radius alone describes a
  // circle around nowhere. The bar printed "Within 10 m" off the default radius for a block whose
  // target was never set, while the feedback panel called the same state Unreachable. Both ask
  // `isGpsSetupIncomplete` now.
  const barEnvelope = (st: FeedbackState, category: 'correct' | 'incorrect') => ({
    id: st.id, category, active: firedIds.has(st.id),
    unreachableLabel: t('blocks.feedbackPanel.unreachable'),
    connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
  });
  // Nothing is correct outside graded, so the bar has no Correct chip to draw.
  const correctState = participation ? undefined : find('correct');
  if (correctState) {
    chips.push(...toTestBarChips(gpsChips('correct', d, t, locale, d.gradingMode), barEnvelope(correctState, 'correct')));
  }

  // The PRESET states the authored condition on the bar too, the same chips the panel shows and
  // the same ones graded's Correct row draws — never "= ?" from a trigger that holds no value.
  for (const s of visibleTargeted(states, d.gradingMode)) {
    if (s.trigger === SET_ANSWER_TRIGGER) {
      chips.push(...toTestBarChips(gpsChips('correct', d, t, locale, d.gradingMode), {
        id: s.id, category: 'targeted', active: firedIds.has(s.id),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
      continue;
    }
    chips.push({
      id: s.id,
      condition: gpsTargetedChipLabel(s.trigger, d, t, locale),
      evaluation: displayEvaluation(d.gradingMode, s.evaluation),
      active: firedIds.has(s.id),
      category: 'targeted',
    });
  }


  if (participation) {
    const catchAll = find(d.gradingMode === 'optional' ? 'any_response' : 'any_answer');
    if (catchAll) {
      /*
       * OPTIONAL is ONE ROW. Everything accumulated above — the gates, the targeted loop — belongs
       * to a mode that can fire them, and optional fires none of them: it is inert, so Any Response
       * is the only outcome it has. Cleared rather than never-pushed, because the rungs above are
       * shared with survey, which does fire them. `legendStatesFor` makes the same call for the
       * blocks that build their legend through it.
       */
      if (d.gradingMode === 'optional') chips.length = 0;

      // The SAME derivation the panel's catch-all uses, so the bar and the card cannot disagree
      // about what still falls through. It enumerates once Set Answer carves the accepted band
      // out, and says the plain label when nothing has. The tick goes the moment a target exists.
      const targetedList = visibleTargeted(states, d.gradingMode);
      chips.push(...toTestBarChips(
        catchAllChips(d.gradingMode, targetedList.length > 0, t,
          () => (targetedList.some(x => x.trigger === SET_ANSWER_TRIGGER)
            ? gpsChips('incorrect', d, t, locale, d.gradingMode)
            /* `catchAllTitle`, not the raw key: with a target authored the leftovers are no
               longer "any answer", and this said they were. The panel beside it has always
               called the same card "Any Other Answer" — see `catchAllTitle`. */
            : [{ kind: 'chip' as const,
                 label: catchAllTitle(d.gradingMode, targetedList.length > 0, t)
                   ?? t('blocks.feedbackDefaults.anyAnswerTitle'),
                 evaluation: 'neutral' as const, sourceId: CATCH_ALL_SOURCE }])),
        {
          id: catchAll.id, category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
          active: firedIds.has(catchAll.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
    }
    return { testResult, testChips: chips, hasChips: chips.length > 0, matchedBranchIndex };
  }

  // Says what actually falls through to the catch-all. `gpsChips` owns the null-radius fallback and
  // the unreachable case, so this no longer risks pushing a chip with no text.
  const incorrectState = find('incorrect');
  if (incorrectState) {
    chips.push(...toTestBarChips(gpsChips('incorrect', d, t, locale, d.gradingMode), barEnvelope(incorrectState, 'incorrect')));
  }

  return { testResult, testChips: chips, hasChips: chips.length > 0, matchedBranchIndex };
}

/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderGpsFiredFeedback(testResult: FeedbackState[] | null): React.ReactNode {
  return <FiredFeedback states={testResult} className='mt-3 flex flex-col gap-2' />;
}

// The readout treatment Compass and Angle both use for their live value. Shared verbatim so the
// three sensor blocks read as one family rather than three house styles. One step down from their
// `text-5xl` deliberately: a coordinate is a PAIR of ~10-character values where a heading is a single
// ~6-character one, and at 48px two such lines overflow a phone-width card.
/** The type scale only — weight and colour come from `sensorReadoutTone`, which is shared with
 *  Compass and Tilt so that committing a reading looks the same on all three. */
const GPS_READOUT_CLASS = 'select-none font-mono text-4xl tabular-nums';

/** One coordinate written per the teacher's chosen format. `cardinal` drops the sign and states the
 * hemisphere instead, which is how coordinates are read aloud and written on a field sheet;
 * `decimal` keeps the signed number the Latitude/Longitude fields are typed in. */
function formatCoord(
  value: number,
  axis: 'lat' | 'lng',
  format: GPSCoordinateFormat,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  if (format === 'decimal') return fmtCoord(value, locale);
  // 0 and -0 are both the equator/prime meridian; Object.is would split them into S/W vs N/E for a
  // difference no instrument can express, so a plain >= 0 test is what's wanted here.
  const positive = value >= 0;
  const letter = t(axis === 'lat'
    ? (positive ? 'blocks.gps.hemisphereNorth' : 'blocks.gps.hemisphereSouth')
    : (positive ? 'blocks.gps.hemisphereEast' : 'blocks.gps.hemisphereWest'));
  return t('blocks.gps.coordCardinal', { value: fmtCoord(Math.abs(value), locale), hemisphere: letter });
}

/**
 * The readout said aloud, with each number named.
 *
 * Unlike Compass and Angle — one value, and a bare "90°" is unambiguous — a GPS reading is a PAIR, and
 * in `decimal` format it renders as two bare signed numbers with nothing but their order to say which
 * axis is which. Sighted students get that order from the layout; a screen reader hears "40.71283
 * negative 74.00601". So the axis names are carried in the DOM, sr-only, rather than left implicit.
 *
 * One function for both the visible readout's hidden labels and the capture announcement, so the two
 * can't describe the same reading differently.
 */
function gpsAxisLabel(axis: 'lat' | 'lng', t: (k: string) => string): string {
  return t(axis === 'lat' ? 'blocks.gps.readoutLatLabel' : 'blocks.gps.readoutLngLabel');
}

/** Live Reading with no fix yet. Two dash lines rather than one, and at the readout's own type size,
 * so the widget doesn't jump when a coordinate arrives — same em-dash convention Compass and Angle
 * use for their own no-reading state. Format-independent: with no fix there is no hemisphere to name. */
function GpsReadoutPlaceholder({ t }: {
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  /**
   * Dashes in EVERY mode, Capture included.
   *
   * Capture used to render this `invisible` — height reserved, paint dropped — on the reasoning that
   * a coordinate above an unpressed button claims a reading already exists. But dashes are not a
   * coordinate; they are the widget saying it is waiting, which is the opposite claim. What the
   * suppression actually produced was a tall blank gap that reads as a broken layout, and because it
   * came with `aria-hidden`, a screen-reader user got nothing at all where a sighted one at least got
   * the gap. The dashes carry sr-only axis labels, so now both are told the same thing.
   *
   * The layout-jump problem the reserve was solving is still solved: the dashes occupy exactly the
   * height the real readout will.
   */
  return (
    <div className='flex flex-col items-center gap-0.5'>
      {/* Named even while empty: two identical dash lines are indistinguishable read aloud, and the
          labels are what tell a student the widget is waiting on a fix for BOTH axes. */}
      <span className={`${GPS_READOUT_CLASS} opacity-40`}>
        <span className='sr-only'>{gpsAxisLabel('lat', t)} </span>
        <SpokenPlaceholder>{t('blocks.gps.liveReadPlaceholder')}</SpokenPlaceholder>
        <SpokenPause />
      </span>
      <span className={`${GPS_READOUT_CLASS} opacity-40`}>
        <span className='sr-only'>{gpsAxisLabel('lng', t)} </span>
        <SpokenPlaceholder>{t('blocks.gps.liveReadPlaceholder')}</SpokenPlaceholder>
      </span>
      {/* Matches GpsReadout's accuracy line, which only exists once there is a reading to be accurate
          about. Without it the placeholder is a line shorter than what replaces it. */}
      <span aria-hidden='true' className='mt-1 text-xs'>&nbsp;</span>
    </div>
  );
}

/** The coordinate readout — the same shape in every mode, because what changes between modes is WHEN
 * a reading exists, never how one is presented once it does. */
function GpsReadout({ reading, format, tone, t, locale }: {
  reading: GpsSimReading;
  format: GPSCoordinateFormat;
  /** Weight and colour, from `sensorReadoutTone` — muted while the fix is merely live, full
   *  strength once it is the student's answer. */
  tone: string;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
}) {
  return (
    <div className='flex flex-col items-center gap-0.5'>
      {/* Latitude above longitude, each on its own line: a single line reads as one number at this
          size, and the two axes are what the student is being asked to get right. */}
      {/* The axis name is sr-only INSIDE each value's own span rather than a sibling, so reading order
          is guaranteed and no absolutely-positioned child can perturb the flex column. */}
      <span className={`${GPS_READOUT_CLASS} ${tone}`}>
        <span className='sr-only'>{gpsAxisLabel('lat', t)} </span>
        {formatCoord(reading.lat, 'lat', format, t, locale)}
        <SpokenPause />
      </span>
      <span className={`${GPS_READOUT_CLASS} ${tone}`}>
        <span className='sr-only'>{gpsAxisLabel('lng', t)} </span>
        {formatCoord(reading.lng, 'lng', format, t, locale)}
        <SpokenPause />
      </span>
      <span className='mt-1 text-xs text-ws-muted'>{t('blocks.gps.pendingCaptureAccuracy', { accuracy: fmtLocale(reading.accuracy, locale) })}</span>
    </div>
  );
}

/** The authored target dressed up as a reading, for the unselected card's inactive summary — it's the
 * coordinate a student standing in the right place would in fact be shown. */
function targetAsReading(d: GpsBlockData): GpsSimReading | null {
  const lat = parseFloat(d.targetLat), lng = parseFloat(d.targetLng);
  if (!isValidCoord(lat, lng)) return null;
  return { lat, lng, accuracy: GPS_GOOD_ACCURACY, distanceMeters: 0, looksNetworkBased: false };
}

/**
 * THE student surface: exactly what a student sees and does, and nothing else.
 *
 * This component is the mechanism behind the standing rule that the live-test card, Preview and the
 * (not yet built) student runtime are the SAME block. All of them render this, unmodified — so
 * anything added here reaches students, and anything a student must never see belongs in
 * `GpsTestWidget` instead. Before this existed each surface assembled its own question + control +
 * feedback, and they had already drifted three ways: the phone Test tab rendered no question at all,
 * Preview rendered the question through raw innerHTML while the canvas card used `PreviewHtml`, and
 * the unselected card re-implemented the visibility-mode branching by hand (and was twice fixed
 * separately from the real widget).
 *
 * Order is fixed and is the student's reading order: question, then the control/readout the mode
 * calls for, then whatever feedback fired.
 */
function GpsAnswerDisplay({
  d, reading, liveReading, hasReading, onCapture, onClearReading, deviceNotice, t, locale, firedFeedback, disabled, scenario,
}: {
  d: GpsBlockData;
  /** The leader's variant, ghosted, above the question — see `AnswerDisplayFrame`'s own note.
   *  A follower's stem lives in another block, which under test is a screen away. */
  scenario?: React.ReactNode;
  /** A device failure that stopped a reading existing at all — permission refused, no fix, timed out.
   * Shown in EVERY visibility mode, Capture on Submit included: that mode has no sensor control of
   * its own, so without this a student whose capture failed sees an unchanged block and no reason. */
  deviceNotice?: string | null;
  reading: GpsSimReading | null;
  /**
   * What the receiver has RIGHT NOW, whether or not it has been captured.
   *
   * Separate from `reading`, which Capture withholds until its button is pressed — that gate is
   * right for the evaluation and wrong for the display, which has to be able to show a muted live
   * fix before the press. Without it the pre-capture state was two dashes, and a student could not
   * tell a receiver with no fix from one with a perfectly good fix they had not taken yet.
   */
  liveReading?: GpsSimReading | null;
  hasReading: boolean;
  onCapture?: () => void;
  /** Discards the committed fix and puts the live readout back — see SensorCaptureButton. */
  onClearReading?: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  firedFeedback?: React.ReactNode;
  /** Non-interactive summary for the unselected canvas card: same markup, nothing clickable.
   * Named to match MCQAnswerDisplay/DropdownAnswerDisplay's own prop. Also aria-hides, which those
   * two do not need — this renders INSIDE the card's activation button. */
  disabled?: boolean;
}) {
  /**
   * A fix too imprecise to grade against, flagged while the student can still do something about it.
   *
   * The `poor_accuracy` feedback state already existed and is terminal in evaluateGPS, but it only
   * ever appeared AFTER submission — so the block showed the accuracy figure, said nothing about it
   * being unusable, and let the attempt be spent. The readout's metres were the only clue, and they
   * mean nothing to a student who does not know the cutoff.
   *
   * Reads the cutoff off the block rather than a shared constant, unlike Compass's two fixed gates:
   * GPS's is teacher-configurable, so what counts as poor is per-block.
   */
  /**
   * Whether what is on screen is the student's ANSWER rather than the receiver still reading.
   *
   * Live Reading counts as committed even though nothing was pressed: it updates forever and it is
   * graded as it stands, so muting it would promise a commit step that mode does not have.
   */
  const committed = d.visibilityMode !== 'capture' || hasReading;
  /** The one fix on screen, whatever its provenance — read once so the readout and the
   *  announcement can never disagree about what was committed. */
  const shownReading = reading ?? (d.visibilityMode === 'capture' ? liveReading ?? null : null);
  const cutoff = parseFloat(d.accuracyCutoff);
  const accuracyPoor = !!reading && Number.isFinite(cutoff) && reading.accuracy > cutoff;
  // Shown in EVERY capture mode, Capture on Submit included. This was briefly gated to the modes
  // with a visible readout, by analogy with Coordinate Format beside it in the drawer -- but that
  // option is moot there because no coordinate is displayed, and a tolerance is not about display.
  // The case in fact runs the other way: with a live readout a student can walk, re-read and
  // iterate, while on submit they are blind and get one shot, so the required precision is the only
  // guidance they have about how careful to be. It is actionable without a readout because the
  // TARGET comes from the question; the readout only ever helped them verify.
  // Through the registry, not off the field: the stored value survives a mode that takes the
  // setting away, so a block carrying `showTolerance: true` into Optional used to print its
  // accepted radius to students one line above "Any Location Accepted".
  const toleranceHintText = effectiveSetting('showTolerance', d.gradingMode, d.showTolerance)
    ? gpsToleranceHint(parseFloat(d.toleranceRadius), t, locale)
    : null;

  return (
    <div
      className={`flex flex-col gap-3 ${disabled ? 'pointer-events-none' : ''}`}
      aria-hidden={disabled || undefined}
    >
      {scenario}
      <BlockQuestion html={d.question} />

      {/* GUIDANCE, so it sits with the instruction and above the instrument -- never after a result.
          It was briefly below the readout, where it landed directly under the accuracy line and in
          the identical `text-xs text-ws-muted` treatment: two grey lines saying opposite kinds of
          thing, one what the teacher requires and one what the device managed. Position separates
          them where styling could not, and the button and coordinates now sit between the two.
          Undimmed with the question for the same reason -- this is part of what is being asked,
          not part of the answer apparatus that dims on an unselected card. */}
      {toleranceHintText && <p className={`text-center ${TOLERANCE_HINT_CLASS}`}>{toleranceHintText}</p>}

      {/* Everything below the question dims on the collapsed card: the reading shown there is
          representative, not a live fix. The question is NOT dimmed — it is the same authored
          text on every surface, and dimming it also faded its variable chips to 50% in these
          three blocks and no others. */}
      {/*
        * THE SAME COLUMN THE OTHER BLOCKS GET, which this one was never given.
        *
        * Seven blocks route their answer through `AnswerDisplayFrame`, which caps and centres
        * it; the three sensors and FITB hand-roll the frame's shape and so were full-width and
        * left-aligned. The instrument itself was centred by its own `mx-auto w-fit` and looked
        * fine, which is why only the FEEDBACK under it read as out of place — it was the one
        * part wide enough to show the difference.
        *
        * The class and not the frame, because the frame also opens a `BlockPreviewProvider`,
        * and Preview deliberately supplies one roll for the whole page — see this block's own
        * note on not nesting it. `ordering-block` applies the column the same way and for the
        * same reason.
        */}
      <div className={`${ANSWER_COLUMN_CLASS} flex flex-col gap-3 ${disabled ? 'opacity-50' : ''}`}>

        {/* Capture on Submit shows the student no control at all — the device reads on submit. */}
        {d.visibilityMode !== 'passive' && (
          <div className='mx-auto flex w-fit flex-col items-center gap-2'>
            {/* Live Reading has no capture button by definition — the coordinate is simply on screen,
                and it is the answer. Capture is the only mode with a button to press. */}
            {d.visibilityMode === 'capture' && (
              <SensorCaptureButton
                committed={committed}
                captureLabel={t('blocks.gps.captureButton')}
                icon={<Crosshair />}
                onCapture={onCapture}
                onReset={onClearReading}
                disabled={disabled}
              />
            )}
            {/*
              ONE readout, muted until it is the student's answer.
              
              Before this, Capture mode showed the same full-strength coordinate before and after
              the press, so the only evidence the button had done anything was that the numbers
              stopped moving — which, standing still, is no evidence at all. The muted step also
              gives a student something the dashes could not: proof the receiver has a fix, while
              they can still walk somewhere with a better one. Dashes now mean only what they say,
              that there is no fix at all. See `sensorReadoutTone`.
            */}
            {shownReading
              ? <GpsReadout reading={shownReading} format={d.coordinateFormat} tone={sensorReadoutTone(committed)} t={t} locale={locale} />
              : <GpsReadoutPlaceholder t={t} />}
            {/* The announcement, and the only live region here — this column used to be one itself,
                which in Live Reading meant one polite announcement per fix the receiver reported. */}
            <SensorAnnounce value={d.visibilityMode === 'capture' && hasReading && shownReading
              ? `${gpsAxisLabel('lat', t)} ${formatCoord(shownReading.lat, 'lat', d.coordinateFormat, t, locale)}, `
                + `${gpsAxisLabel('lng', t)} ${formatCoord(shownReading.lng, 'lng', d.coordinateFormat, t, locale)}`
              : null} />
          </div>
        )}

        {/* Both warnings sit out here rather than inside the readout column, so they share the outer
            column's spacing. The network notice used to live inside it, tucked under the accuracy line
            at 2px where this sits at 12px, which read as two different kinds of message.

            One at a time, accuracy first. A network-based fix is a CAUSE of poor accuracy, so when the
            accuracy gate is already tripped the advice to go outside covers both, and stacking a second
            warning would add words without adding an action. The network notice then keeps the case it
            exists for: a reading precise enough to look trustworthy that still is not real GPS. */}
        {deviceNotice ? (
          // Ahead of the quality advisories: there is no reading to judge the quality of.
          <SensorAdvisory advice={deviceNotice} />
        ) : accuracyPoor ? (
          <SensorAdvisory
            label={t('blocks.gps.accuracyAdviceLabel')}
            advice={t('blocks.gps.accuracyAdvice')}
          />
        ) : reading?.looksNetworkBased ? (
          // The STUDENT's wording, distinct from the teacher's answer-setup capture. The two used to
          // share one string, which meant whichever audience it was written for, the other read advice
          // aimed at somebody else: a teacher picking a target is not submitting anything.
          <SensorAdvisory advice={t('blocks.gps.readingNetworkBasedWarning')} />
        ) : null}

        {firedFeedback}
      </div>
    </div>
  );
}

/** The simulate hint's distance. Rounds to whole metres first — unlike an authored tolerance, this is
 * a measured float, and "16.6273 m from the target" is noise — then defers to the same metre/kilometre
 * switch every chip and the answer bar use, so no two surfaces disagree about the unit. */
function gpsDistanceHint(
  meters: number,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  const rounded = Math.round(meters);
  const km = gpsUseKm(rounded);
  return t(km ? 'blocks.gps.simulateWillReadKm' : 'blocks.gps.simulateWillRead', {
    distance: gpsDistanceValue(rounded, km, locale),
  });
}

/** The teacher's test harness wrapped around `GpsAnswerDisplay`: a Simulate strip, plus the note
 * explaining Capture on Submit's deliberately empty student surface.
 *
 * Everything in here is teacher-only and must stay out of the student widget. The strip shows in all
 * three visibility modes — it is the only way the block gets tested at all, so hiding it in Capture
 * on Submit would make that mode untestable. It also shows in Preview: without real sensors there is
 * no other way to drive a reading there, and it is the single acknowledged place where Preview is not
 * yet pixel-identical to what a student will get. */
function GpsTestWidget({
  d, selection, onSelectionChange, mode, onModeChange, deviceStatusHint, deviceNotice, manual, reading, liveReading, hintReading, onCapture, onClearReading, hasReading, t, locale, children, sharedDevice, scenario
}: {
  /** The leader's variant, ghosted, above the question — forwarded to the display. */
  scenario?: React.ReactNode;
  /**
   * Preview drives this block from the task's ONE shared device, so the per-block strip
   * comes out: three strips in Preview model three phones, which is the thing the shared
   * simulator exists to stop. The strip stays in the EDITOR, where a teacher is testing one
   * block against its own answer in isolation and that is exactly what they want.
   */
  sharedDevice?: boolean;
  d: GpsBlockData;
  selection: GpsSimSelection;
  onSelectionChange: (next: GpsSimSelection) => void;
  mode: SensorSourceMode;
  onModeChange: (next: SensorSourceMode) => void;
  /** What the real receiver is doing, or why it cannot be read here. Only consulted in device mode. */
  deviceStatusHint: string;
  /** Device failures only, for the student surface. `deviceStatusHint` also carries "waiting for a
   * fix", which is a status rather than something to warn a student about. */
  deviceNotice: string | null;
  manual: {
    lat: string; lng: string;
    onLatChange: (v: string) => void; onLngChange: (v: string) => void;
    latInvalid: boolean; lngInvalid: boolean;
  };
  reading: GpsSimReading | null;
  /** Ungated: what the armed selection would report, for the always-visible hint. */
  hintReading: GpsSimReading | null;
  onCapture: () => void;
  onClearReading: () => void;
  /** The uncaptured receiver value — see GpsAnswerDisplay's own prop. */
  liveReading?: GpsSimReading | null;
  hasReading: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  children?: React.ReactNode;
}) {
  const setupIncomplete = isGpsSetupIncomplete(d);
  const isNoReading = selection.kind === 'scenario' && selection.id === 'noReading';
  // An existing reading is described first, ahead of the incomplete-setup notice. A manually typed
  // point produces one even with no target authored, and telling a teacher to "set a target" while
  // the readout is showing them their own point contradicts what is on screen.
  // In device mode the line is the sensor's own story — acquiring, unavailable, no permission — and
  // none of the simulate reasoning applies, since every simulated setting is being ignored.
  const hint = mode === 'device' ? deviceStatusHint
    : hintReading
    ? hintReading.distanceMeters == null
      ? t('blocks.gps.simulateManualNoTarget')
      : gpsDistanceHint(hintReading.distanceMeters, t, locale)
    : setupIncomplete ? t('blocks.gps.simulateNeedsAnswer')
    : isNoReading ? t('blocks.gps.simulateWillReadNothing')
    : '';

  return (
    <div className='flex flex-col gap-3'>
      {!sharedDevice && (
      <SensorSimulateBar
        groupLabel={t('blocks.sensorSource.scenarioGroup')}
        scenarios={(setupIncomplete ? GPS_DEVICE_SCENARIO_IDS : GPS_SCENARIO_IDS).map(id => ({ id, label: t(`blocks.gps.simulateScenario.${id}`) }))}
        activeScenarioId={selection.kind === 'scenario' ? selection.id : null}
        onPickScenario={id => onSelectionChange(id === '' ? { kind: 'none' } : { kind: 'scenario', id: id as GpsScenarioId })}
        manual={{
          label: t('blocks.gps.simulateManualLabel'),
          active: selection.kind === 'manual',
          fields: [
            {
              key: 'lat',
              ariaLabel: t('blocks.gps.fieldTargetLat'),
              placeholder: t('blocks.gps.simulateManualLatPlaceholder'),
              value: manual.lat,
              onChange: manual.onLatChange,
              invalid: manual.latInvalid,
              disabled: isNoReading,
            },
            {
              key: 'lng',
              ariaLabel: t('blocks.gps.fieldTargetLng'),
              placeholder: t('blocks.gps.simulateManualLngPlaceholder'),
              value: manual.lng,
              onChange: manual.onLngChange,
              invalid: manual.lngInvalid,
              disabled: isNoReading,
            },
          ],
        }}
        mode={mode}
        onModeChange={onModeChange}
        deviceTabLabel={t('blocks.sensorSource.device')}
        simulateTabLabel={t('blocks.sensorSource.simulate')}
        hint={hint}
      />
      )}

      <GpsAnswerDisplay
          scenario={scenario}
        d={d}
        reading={reading}
        liveReading={liveReading}
        hasReading={hasReading}
        onCapture={onCapture}
        onClearReading={onClearReading}
        deviceNotice={deviceNotice}
        t={t}
        locale={locale}
        firedFeedback={children}
      />
    </div>
  );
}

function gpsCollapsedBar(
  d: GpsBlockData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  issues: BlockIssue[],
): React.ReactNode {
  // Informational-only, GPS-scoped, no enforcement — shown on every state of this bar per the
  // approved plan, not just the success case, since a teacher scanning the canvas should always
  // be able to tell this block needs a phone to answer regardless of its setup state.
  const mobileChip = <MobileOnlyChip />;

  // Highest-severity issue wins the bar, exactly as orderingCollapsedIssueBar does — errors
  // before warnings, one line, bold category then detail. The mobile chip rides alongside in
  // every state, which is why this can't just call a shared renderer.
  // The mobile chip rides along in every state, which used to be the stated reason this bar
  // couldn't call a shared renderer. It can now: `trailing` is part of the shared bar's own
  // contract, so all three sensor blocks and the six others render one implementation.
  const issueBar = collapsedIssueBar(issues, mobileChip);
  if (issueBar) return issueBar;

  if (d.gradingMode !== 'graded') return collapsedModeBar(d.gradingMode, t, 'gps', mobileChip);

  // Raw-answer style (matches numerical-block.tsx's collapsed bar), not a chip/pill — this
  // project is settling on plain text for "here's the correct answer" displays rather than
  // slider's chip convention. Per the approved scope, this change is GPS-only; slider and other
  // blocks aren't being retrofitted here.
  return (
    <CollapsedBlockBar tone='success' align='start' scrollX trailing={mobileChip}>
      <span className='min-w-0'>
        {(() => {
          const tol = parseFloat(d.toleranceRadius);
          const km = gpsUseKm(tol);
          // With branches there is no single coordinate to print — which target applies is the
          // question the equation answers — so the bar states the SHAPE of the answer instead of
          // inventing one of them. The radius still applies to all of them, so it stays.
          const branchCount = d.gradingMode === 'graded' ? (d.conditionalBranches?.length ?? 0) : 0;
          if (branchCount > 0) {
            return t('blocks.gps.answerBarBranchedAnswer', {
              count: branchCount,
              radius: gpsDistanceValue(tol, km, locale),
            });
          }
          return t(km ? 'blocks.gps.answerBarCorrectAnswerKm' : 'blocks.gps.answerBarCorrectAnswer', {
            lat: fmtCoord(parseFloat(d.targetLat), locale),
            lng: fmtCoord(parseFloat(d.targetLng), locale),
            radius: gpsDistanceValue(tol, km, locale),
          });
        })()}
      </span>
    </CollapsedBlockBar>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function GPSBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange,
  siblingGpsTargets, blocks, onRename,
}: GPSBlockProps) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Through the version: the block's own top-level target is vestigial once a bank exists. */
  const initial = useRef(applyGPSVersion(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it. The ref is the
   * synchronous read the push paths depend on; a ref cannot re-render, so restored HTML needs
   * state to reach the screen. See keyword, where this was first worked out.
   */
  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedTargetLat, setTargetLat] = useState(initial.current.targetLat);
  const [editedTargetLng, setTargetLng] = useState(initial.current.targetLng);
  const [editedConditionalEquation, setConditionalEquation] = useState(initial.current.conditionalEquation ?? '');
  const [editedConditionalBranches, setConditionalBranches] = useState<EditorLocationBranch[] | undefined>(initial.current.conditionalBranches);
  // Shared with the test bar's "Edit this branch" jump, like the other six branching blocks —
  // GPS was the one that never had the hand-off, so a teacher who saw "Branch 2 applies" had to
  // find branch 2 by hand.
  const [activeBranchIndex, setActiveBranchIndex] = useBranchJumpTarget(block.id);
  const branchPrevButtonRef = useRef<HTMLButtonElement | null>(null);
  const [editedToleranceRadius, setToleranceRadius] = useState(initial.current.toleranceRadius);
  const [editedShowTolerance, setShowTolerance] = useState(initial.current.showTolerance);
  const [editedAccuracyCutoff, setAccuracyCutoff] = useState(initial.current.accuracyCutoff);
  const [editedVisibilityMode, setVisibilityMode] = useState(initial.current.visibilityMode);
  const [editedCoordinateFormat, setCoordinateFormat] = useState(initial.current.coordinateFormat);
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);

  const [isCapturing, setIsCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  // A just-captured reading staged for review before it's committed to targetLat/targetLng —
  // never persisted itself; the teacher inspects lat/lng/accuracy here and explicitly clicks
  // "Use" to commit it (see handleUsePendingCapture), so a poor-accuracy reading never silently
  // overwrites the real target fields. `elapsedMs`/`altitude` exist only to drive the
  // network-fix heuristic below, not for display beyond that.
  const [pendingCapture, setPendingCapture] = useState<{ lat: number; lng: number; accuracy: number; altitude: number | null; elapsedMs: number } | null>(null);
  const [pasteInput, setPasteInput] = useState('');

  // ── Live test state ─────────────────────────────────────────────────────────
  // Same shape as Compass/Angle's — see compass-block.tsx for the reasoning.
  const [testSelection, setTestSelection] = useState<GpsSimSelection>({ kind: 'none' });
  /** What this block handed in last time it was on screen -- see `readingFor` for when it is used. */
  const prior = usePriorAnswer<GpsSimReading>();
  const [restoredReading, setRestoredReading] = useState<GpsSimReading | null>(prior ?? null);
  const [testTaken, setTestTaken] = useState(prior !== undefined);
  const [continuous, setContinuous] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  /** This block's own variable roll, for BLOCK TEST mode only.
   *
   * Preview deliberately does NOT nest this: the task-level BlockPreviewProvider supplies one roll
   * shared by every block on the page, which is the whole point of previewing a task. Testing a
   * single block is the opposite case — its own roll, re-rollable from its own Reroll button. */
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const [rolledValues, setRolledValues] = useState<Record<string, string>>(() => rollVariableValues(variables));
  /**
   * The version THIS student drew, for Preview.
   *
   * GPS does not read the task-preview roll hook the other blocks use — it has its own
   * `numericRoll` — so the drawn version is taken from the session roll directly. A block with no
   * bank gets `undefined`, which `pickVersion` reads as "the only one there is".
   */
  const taskPreviewRoll = useTaskPreviewRoll();
  const previewVersionId = taskPreviewRoll?.[versionRollKey(block.id)];
  // resolveMatchedBranch works in numbers; rollVariableValues yields strings. Converted once here
  // rather than at each of the three test surfaces.
  const numericRoll: Record<string, number> = Object.fromEntries(
    Object.entries(rolledValues).map(([k, v]) => [k, parseFloat(v)]).filter(([, n]) => Number.isFinite(n as number))
  );
  // Fresh values each time the block enters a test surface, so opening a block never shows the
  // stale roll from the last time it was open. Mirrors MCQ's own isSelected/phoneTestMode effects.
  useEffect(() => {
    if (isSelected || phoneTestMode === 'test') setRolledValues(rollVariableValues(variables));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, phoneTestMode]);
  /** The last real fix from this device, live only while the Device option is armed. `watchPosition`
   * rather than a one-shot read so all three visibility modes are served by one mechanism: Live
   * Reading shows it updating, Capture takes whatever the latest fix is when the button is pressed,
   * and Capture on Submit does the same at submit. Cleared when Device is disarmed so a stale fix
   * can't masquerade as a fresh one. */
  const [deviceReading, setDeviceReading] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);

  // Bumped on every scenario pick so the randomised value inside a category is redrawn — including
  // re-picking the preset that's already armed. Kept in state rather than derived so the draw is
  // stable across the re-renders a keystroke in the editor causes.
  const [simNonce, setSimNonce] = useState(0);
  /** Scratch text for the simulate strip's manual coordinate boxes — applied as typed, never saved. */
  const [simManualLat, setSimManualLat] = useState('');
  const [simManualLng, setSimManualLng] = useState('');

  /**
   * Which source the block reads from. Separate state from `testSelection`, not one more variant of
   * it: the two are orthogonal now — a teacher who switches to the device and back should find their
   * armed scenario still armed, which a union collapsing both into one value cannot express.
   */
  const [sourceMode, setSourceMode] = useState<SensorSourceMode>('simulate');
  // Non-null only in Preview; the editor keeps its own per-block simulator untouched.
  const shared = useSensorSimulator();
  /**
   * The simulator is reporting no signal at all — the receiver produced nothing.
   *
   * Distinct from a null reading, which is also what a student who has not captured yet has. That
   * one is an ordinary No Answer; this one is the hardware, and the student may have had no way
   * round it.
   */
  const sensorDead = !!shared && !shared.useDevice && shared.signal === 'none';
  /**
   * A failure of the DEVICE-mode watch, kept apart from `captureError`.
   *
   * `captureError` belongs to the teacher's answer-setup capture and renders beside that panel's own
   * button. Routing the watch's failures through it too meant a device problem showed up in the
   * teacher's setup UI and nowhere a student could ever see, which is the wrong half of the app on
   * both counts. Same split, and the same reason, as `deviceUnavailable` on Compass.
   */
  /** The CODE, not the sentence — the sentence for a denial depends on a permission state that can
   *  change after the error, so it is resolved at render. See useGeolocationPermission. */
  const [deviceErrorCode, setDeviceErrorCode] = useState<number | 'generic' | null>(null);
  /**
   * Whether the REAL sensor is being read, which in Preview the shared device decides.
   *
   * This was `sourceMode === 'device'` alone, and that made the consolidated panel's
   * Device switch do nothing: it flipped the shared flag while every block went on reading
   * its own `sourceMode`, which defaults to Simulate. The switch looked wired and was not.
   *
   * The block's own `sourceMode` still governs in the EDITOR, where there is no provider
   * above it and its per-block strip is the thing being operated.
   */
  const deviceArmed = shared ? shared.useDevice : sourceMode === 'device';
  useEffect(() => {
    if (!deviceArmed) { setDeviceReading(null); setDeviceErrorCode(null); return; }
    setDeviceErrorCode(null);
    if (!('geolocation' in navigator)) { setDeviceErrorCode('generic'); return; }
    const id = navigator.geolocation.watchPosition(
      pos => {
        setDeviceReading({ lat: roundCoord(pos.coords.latitude), lng: roundCoord(pos.coords.longitude), accuracy: pos.coords.accuracy });
        // A fix arriving clears an earlier refusal — the student granted it after all.
        setDeviceErrorCode(null);
      },
      err => setDeviceErrorCode(err.code),
      { enableHighAccuracy: true, timeout: 10000 },
    );
    return () => navigator.geolocation.clearWatch(id);
    // The exhaustive-deps exemption this used to carry is gone with its reason: the effect stored
    // a translated sentence and so depended on `t`. It stores the raw code now and the sentence is
    // built at render, so the dep list is genuinely complete.
  }, [deviceArmed]);

  /**
   * What the real receiver is doing, for the hint line in device mode.
   *
   * `captureError` is reused rather than duplicated: the watch and the one-shot capture fail for the
   * same reasons (denied, unavailable, timed out) and already funnel their messages there, so a
   * second parallel string would only be a way for the two to disagree.
   */
  /*
   * The sentence for a refusal, chosen by whether asking again would do anything.
   *
   * `PERMISSION_DENIED` is both "dismissed the prompt" and "said no and the browser remembered".
   * The first is one tap from fixed; the second needs browser settings and no retry button will
   * help. Telling a student to try again when it cannot work is worse than saying nothing.
   */
  const geoPermission = useGeolocationPermission(deviceArmed);
  const deviceError = deviceErrorCode == null ? null
    : deviceErrorCode === 'generic' ? t('blocks.gps.captureErrorGeneric')
    : deviceErrorCode === 1
      ? t(geoPermission === 'denied' ? 'blocks.gps.captureErrorPermissionBlocked' : 'blocks.gps.captureErrorPermissionDenied')
    : deviceErrorCode === 2 ? t('blocks.gps.captureErrorPositionUnavailable')
    : deviceErrorCode === 3 ? t('blocks.gps.captureErrorTimeout')
    : t('blocks.gps.captureErrorGeneric');
  const deviceStatusHint = deviceError ?? (deviceReading ? '' : t('blocks.gps.simulateDeviceWaiting'));
  /** Only real failures reach the student surface — "waiting for a fix" is a status, not a problem. */
  const studentDeviceNotice = deviceArmed ? deviceError : null;

  function handleTestCapture() {
    setTestTaken(true);
    // Frozen at the moment of capture, so the committed fix stops moving — the whole meaning of the
    // full-strength readout it switches on.
    setCapturedReading(liveReadingFor(currentDataRef.current));
    // Capturing supersedes whatever the revisit put back. Without this the restored fix would
    // outrank the one the student just took, and the button would look broken.
    setRestoredReading(null);
    // `continuous` is a TEST-PANEL affordance, so it must not apply in Preview: Preview has no
    // TestBar to switch it off, and a student pressing capture is not submitting — they'd see the
    // answer before the page-level Submit. In Preview only previewSubmitSignal reveals feedback.
    if (continuous && !isPreview) setHasSubmitted(true);
  }
  /**
   * Reset: throw away the committed fix and let the receiver stream again.
   *
   * Clears the ANSWER, not just the display — see SensorCaptureButton. `restoredReading` goes with
   * it, or a revisit's coordinate would reappear the instant the capture it replaced was discarded.
   * `hasSubmitted` is deliberately left alone: a reset is not an un-submission.
   */
  function handleClearCapture() {
    setTestTaken(false);
    setCapturedReading(null);
    setRestoredReading(null);
  }

  /**
   * Mirrors a scenario's resolved point into the manual boxes.
   *
   * The presets are the reason this block keeps a preset list at all — a point 10 m off target is a
   * coordinate pair nobody can derive in their head — but that also meant the actual numbers being
   * fed to the block were invisible, and the boxes sat empty next to a reading they had not produced.
   * Filling them makes the strip say what it is doing, and leaves the teacher one edit away from
   * nudging it.
   *
   * `nextNonce` is threaded in rather than read from state because the draw is a pure function of the
   * nonce the pick is about to install, and `simNonce` still holds the previous one at this point.
   */
  function syncManualBoxes(next: GpsSimSelection, nextNonce: number) {
    if (next.kind !== 'scenario') return;
    const reading = gpsSimReading(next, currentDataRef.current, nextNonce);
    // No reading means nothing will be passed, so the boxes show nothing. They are disabled in that
    // state too, so this cannot be mistaken for a value waiting to be used.
    setSimManualLat(reading ? String(reading.lat) : '');
    setSimManualLng(reading ? String(reading.lng) : '');
  }

  function handleTestSelectionChange(next: GpsSimSelection) {
    // Choosing "Specific Location" while the boxes hold a usable point arms THAT point, rather than
    // dropping to no-reading while a perfectly good pair of coordinates sits on screen. It is also
    // what makes stepping back off a preset non-destructive: the preset's own autofilled point stays
    // armed until the teacher changes it.
    if (next.kind === 'none') {
      const la = parseFloat(simManualLat), ln = parseFloat(simManualLng);
      if (isValidCoord(la, ln)) {
        setTestSelection({ kind: 'manual', lat: roundCoord(la), lng: roundCoord(ln) });
        if (!continuous) setHasSubmitted(false);
        return;
      }
    }
    setTestSelection(next);
    if (next.kind === 'scenario') {
      const nextNonce = simNonce + 1;
      setSimNonce(nextNonce);
      syncManualBoxes(next, nextNonce);
    }
    if (!continuous) setHasSubmitted(false);
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    // Only Capture needs the flag; evaluatedFor() derives the other two from `continuous` directly,
    // and setting it here would leave their feedback stuck on after continuous is switched back off.
    if (next && !isPreview && visibilityMode === 'capture' && testTaken) setHasSubmitted(true);
  }
  function handleTestSubmit() { setHasSubmitted(true); }
  /** Another draw inside the armed scenario — see sensorSimRandom. */
  /** Another draw inside the armed scenario AND a fresh variable roll — the test panel has one
   * Reroll button and it means "give me a different randomised setup", which covers both. */
  function handleSimReroll() {
    const nextNonce = simNonce + 1;
    setSimNonce(nextNonce);
    // A reroll redraws the scenario, so the boxes have to follow it or they would keep showing the
    // previous draw's coordinates while the readout moved on.
    syncManualBoxes(testSelection, nextNonce);
    setRolledValues(rollVariableValues(variables));
  }
  function handleTestReset() { setTestTaken(false); setHasSubmitted(false); setRestoredReading(null); }

  // Preview's single page-level Submit — reacts to the signal CHANGING, not a per-block click,
  // reusing the same test state the live test widget already drives.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) setHasSubmitted(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);


  const sharedToolbar = useSharedEditorToolbar();
  // No `useFocusedField`: the shared fields decide when a fault is visible, by the one rule.
  const { setActiveEditor, activeEditorRef } = sharedToolbar;

  // Every plain <input> in this block focuses through here — see onPlainFieldFocus's own doc
  // comment for why leaving the shared toolbar pointed at the Question editor is a real bug.
  const disconnectSharedToolbar = sharedToolbar.disconnect;
  const questionEditorRef = useRef<Editor | null>(null);

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml, targetLat: editedTargetLat, targetLng: editedTargetLng,
    toleranceRadius: editedToleranceRadius, showTolerance: editedShowTolerance,
    accuracyCutoff: editedAccuracyCutoff, visibilityMode: editedVisibilityMode,
    coordinateFormat: editedCoordinateFormat, gradingMode: editedGradingMode,
    conditionalEquation: editedConditionalEquation, conditionalBranches: editedConditionalBranches,
  };
  const restored = useRestoredData<GpsBlockData>({
    raw: block.data,
    isEditing,
    derive: (raw) => applyGPSVersion(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: { ...applyGPSVersion(initData(block.data), editorVersion), ...edited } as GpsBlockData,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: GpsBlockData) {
      setTargetLat(next.targetLat);
      setTargetLng(next.targetLng);
      setToleranceRadius(next.toleranceRadius);
      setShowTolerance(next.showTolerance);
      setAccuracyCutoff(next.accuracyCutoff);
      setVisibilityMode(next.visibilityMode);
      setCoordinateFormat(next.coordinateFormat);
      setGradingMode(next.gradingMode);
      setConditionalEquation(next.conditionalEquation ?? '');
      setConditionalBranches(next.conditionalBranches);
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.(next.feedback);
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(applyGPSVersion(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(applyGPSVersion(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = (branchId?: string) => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump(branchId);
  };
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const targetLat = isEditing ? editedTargetLat : restored.data.targetLat;
  const targetLng = isEditing ? editedTargetLng : restored.data.targetLng;
  const toleranceRadius = isEditing ? editedToleranceRadius : restored.data.toleranceRadius;
  const showTolerance = isEditing ? editedShowTolerance : restored.data.showTolerance;
  const accuracyCutoff = isEditing ? editedAccuracyCutoff : restored.data.accuracyCutoff;
  const visibilityMode = isEditing ? editedVisibilityMode : restored.data.visibilityMode;
  const coordinateFormat = isEditing ? editedCoordinateFormat : restored.data.coordinateFormat;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const conditionalEquation = isEditing ? editedConditionalEquation : (restored.data.conditionalEquation ?? '');
  const conditionalBranches = isEditing ? editedConditionalBranches : restored.data.conditionalBranches;

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addGPSVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addGPSVersion });

  /** Every field write, routed into the version it belongs to — see mcq-block.tsx's note. */
  const emitFields = (next: Partial<GpsBlockData>) => {
    const base = initData(block.data);
    emit(writeGPSVersion(base, editorVersion, { ...base, ...next } as GpsBlockData) as unknown as Record<string, unknown>);
  };

  // Transient equation defects wait for the equation to stop changing. Two filters, not one: the
  // card and test panel read a list built from `block.data`, the editor panel one built from live
  // state, and they are often rendered by different instances of this component.
  const { settleLive, settleStored } = useIssueSettling(
    { equation: conditionalEquation },
    { equation: initData(block.data).conditionalEquation },
  );

  const fields: GpsBlockData = {
    question: questionHtml, targetLat, targetLng,
    toleranceRadius, showTolerance, accuracyCutoff, visibilityMode, coordinateFormat, gradingMode,
    feedback: { states: [] }, // placeholder — real feedback pushed via currentDataRef below
  };
  const currentDataRef = useRef(fields);
  currentDataRef.current = fields;

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle =
    gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle') :
    gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle') :
    t('blocks.feedbackDefaults.anyResponseTitle');

  const {
    feedback, setFeedback, resyncFeedback, getTitle,
  } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      // Sensor blocks say "measurement", not "question": a student who skips one of these hasn't
      // failed to answer, they've failed to TAKE A READING, and the feedback should name the thing
      // they actually have to do. Shared across GPS/Compass/Angle so the three can't drift apart.
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noMeasurementContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
      extra: {
        [NO_SENSOR_DATA_TRIGGER]: t('blocks.sensors.noSensorDataFeedback'),
        poor_accuracy: t('blocks.gps.poorAccuracyFeedback'),
      },
    },
    extraDefaults: [{
      // Ordered as the evaluator gates them: no reading at all, then a reading too poor to grade.
      trigger: NO_SENSOR_DATA_TRIGGER,
      title: t('blocks.sensors.noSensorDataFeedback'),
      content: `<p>${t('blocks.gps.noSensorDataDefault')}</p>`,
      evaluation: 'neutral',
    }, {
      trigger: 'poor_accuracy',
      title: t('blocks.gps.poorAccuracyFeedback'),
      content: `<p>${t('blocks.gps.poorAccuracyDefault')}</p>`,
      evaluation: 'neutral',
    }],
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      emitFields({ ...currentDataRef.current, feedback: nextFeedback });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;

  function push(overrides: Partial<GpsBlockData> = {}) {
    emitFields({ ...currentDataRef.current, feedback, ...overrides });
  }

  /** Every path that moves the target goes through here, so no caller can forget the resync. */
  // Branching is graded-only, like every other branching block: in Participation or Optional there
  // is no correct answer for a branch to select.
  const branchingOn = gradingMode === 'graded' && !!conditionalBranches && conditionalBranches.length > 0;
  // Same three-way read the six content blocks use: blank, present-but-unparseable, or usable.
  // `equationWorks` is what dims the conditions and the target below them, since neither means
  // anything until the selector resolves to a number.
  const gpsHasEquation = !!conditionalEquation.trim();
  const gpsEquationInvalid = gpsHasEquation && evaluateEquation(conditionalEquation, minSampleValues(variables), angleModeFor(initData(block.data), 'conditionalEquation')) === null;
  const gpsEquationWorks = gpsHasEquation && !gpsEquationInvalid;
  const clampedBranchIndex = branchingOn ? Math.min(activeBranchIndex, conditionalBranches!.length - 1) : 0;
  const activeBranch = branchingOn ? conditionalBranches![clampedBranchIndex] : undefined;

  /**
   * FOLLOWING A SCENARIO. `ScenarioLocationBranch` and GPS's half of
   * `ordering-gps-scenario.test.ts` have been in the package since the family port; only the
   * editor was missing, so a GPS block could be graded against a scenario it had no way to be
   * linked to.
   *
   * GPS ports with the branching family rather than with the numeric blocks for the reason the
   * engine comment gives: a location is a place you pick, not a value you compute. The payload
   * is a TARGET, which is exactly what its conditional branch carries.
   */
  const scenario = useScenarioFollower<EditorScenarioLocation>({
    block, blocks, gradingMode, feedback, isEditing, isPreview,
    storedData: initData(block.data) as never,
    restoredRows: (restored.data as GpsBlockData).scenarioBranches,
    initialRows: (initial.current as GpsBlockData).scenarioBranches,
    emit: data => emit(versionStructure(block.data, data)),
    applyVersion: applyGPSVersion,
    emitFields: next => emitFields(next as Partial<GpsBlockData>),
    rowTemplate: () => ({ targetLat, targetLng, feedback }),
    rowFromVersion: v => ({
      targetLat: String((v as unknown as { targetLat?: unknown }).targetLat ?? ''),
      targetLng: String((v as unknown as { targetLng?: unknown }).targetLng ?? ''),
      feedback: (v as unknown as { feedback: { states: FeedbackState[] } }).feedback,
    }),
    numeric: {
      read: () => ({ equation: conditionalEquation, branches: conditionalBranches }),
      write: (eq, br) => {
        // GPS holds the equation as a plain string, '' for absent — unlike the blocks whose
        // state is `string | undefined`. The hook speaks the latter, so it converts here.
        setConditionalEquation(eq ?? '');
        setConditionalBranches(br as EditorLocationBranch[] | undefined);
      },
    },
  });
  /**
   * BRANCH-SCOPED FEEDBACK, the layer the other six branching blocks have had all along.
   *
   * GPS was the one block of seven whose branches carried no feedback of their own, so its
   * panel wrote the block's directly. `ConditionalLocationBranch` gained a `feedback` field
   * once "head for the boathouse" turned out not to be the same sentence as "head for the old
   * oak" — and a field nothing writes is worse than no field, because it looks supported.
   *
   * Reads fall back to the block's for a branch saved before the field existed, which is the
   * same optional chain `evaluateGPS` uses; writes always produce one, so a branch heals the
   * first time it is edited.
   */
  /* WHICHEVER setup the panel is editing — a numeric branch and a scenario row differ only in
     what SELECTS them, and every read below this line is one path. */
  const activeFeedback = scenario.activeScenario?.feedback ?? activeBranch?.feedback ?? feedback;
  const activeFeedbackStates = activeFeedback.states;

  function setActiveFeedbackStates(nextStates: FeedbackState[]) {
    if (scenario.activeScenario) {
      scenario.writeActiveRow({ feedback: { states: nextStates } });
      return;
    }
    if (activeBranch) {
      const next = (conditionalBranches ?? []).map(b =>
        (b.id === activeBranch.id ? { ...b, feedback: { states: nextStates } } : b));
      setConditionalBranches(next);
      push({ conditionalBranches: next });
      return;
    }
    const nextFeedback = { states: nextStates };
    setFeedback(nextFeedback);
    push({ feedback: nextFeedback });
  }

  const updateActiveFeedbackState = (id: string, patch: Partial<FeedbackState>) =>
    setActiveFeedbackStates(activeFeedbackStates.map(st => (st.id === id ? { ...st, ...patch } : st)));

  function deleteActiveFeedbackState(id: string) {
    const st = activeFeedbackStates.find(x => x.id === id);
    // Defaults are structural — the panel offers no delete for them, and this is the floor.
    if (!st || st.type === 'default') return;
    setActiveFeedbackStates(activeFeedbackStates.filter(x => x.id !== id));
  }

  // Only the TARGET follows the branch. Tolerance radius and Site Conditions sit outside the
  // wrapper and are unaffected by which branch is showing.
  /* Same order as `writeTarget` writes in — the field must SHOW the target it would edit, or
     the box displays the block's copy while a keystroke lands on the row. */
  const shownLat = scenario.activeScenario?.targetLat ?? (activeBranch ? activeBranch.targetLat : targetLat);
  const shownLng = scenario.activeScenario?.targetLng ?? (activeBranch ? activeBranch.targetLng : targetLng);

  /**
   * The ONE path that writes a target, whichever control produced it — typing, Capture, or Paste.
   * Routes to the branch currently on screen when branching is on, which is what makes "capture
   * goes to the branch you are looking at" true of every control rather than of whichever ones
   * remembered to check.
   */
  function writeTarget(patch: { targetLat?: string; targetLng?: string }) {
    /* INNERMOST FIRST, and the scenario row comes before the branch for the reason every other
       follower orders them this way: a linked block has no numeric branch to fall through to,
       and the target on screen belongs to the row the pager is showing. */
    if (scenario.activeScenario) {
      scenario.writeActiveRow(patch);
      return;
    }
    if (activeBranch) {
      const next = (conditionalBranches ?? []).map(b => b.id === activeBranch.id ? { ...b, ...patch } : b);
      setConditionalBranches(next);
      pushTargetChange({ conditionalBranches: next });
      return;
    }
    if (patch.targetLat !== undefined) setTargetLat(patch.targetLat);
    if (patch.targetLng !== undefined) setTargetLng(patch.targetLng);
    pushTargetChange(patch);
  }

  function commitBranches(next: EditorLocationBranch[] | undefined) {
    setConditionalBranches(next);
    pushTargetChange({ conditionalBranches: next });
  }
  function updateLocationBranch(id: string, patch: Partial<EditorLocationBranch>) {
    commitBranches((conditionalBranches ?? []).map(b => b.id === id ? { ...b, ...patch } : b));
  }
  function addLocationBranch() {
    const next = [...(conditionalBranches ?? []), {
      id: crypto.randomUUID(), operator: '==' as const, threshold: '',
      // A new branch starts EMPTY rather than copying the current target: two branches silently
      // sharing one location is a setup that looks authored and grades identically either way.
      targetLat: '', targetLng: '',
    }];
    commitBranches(next);
    setActiveBranchIndex(next.length - 1);
  }
  function removeLocationBranch(id: string) {
    const next = (conditionalBranches ?? []).filter(b => b.id !== id);
    commitBranches(next.length > 0 ? next : undefined);
    setActiveBranchIndex(0);
    // The deleted button is gone, so focus would otherwise fall to <body>.
    branchPrevButtonRef.current?.focus();
  }
  /** Turning branching ON seeds branch 1 from the target already authored — otherwise flipping the
   * toggle silently discards a location the teacher may have walked to capture. Turning it OFF
   * leaves the branch data in place, untouched and simply unread, the same way every other
   * branching block treats it. */
  function toggleBranching(on: boolean) {
    if (!on) { commitBranches(undefined); return; }
    commitBranches([{
      id: crypto.randomUUID(), operator: '==' as const, threshold: '',
      targetLat, targetLng,
    }]);
    setActiveBranchIndex(0);
  }

  /**
   * A target moved, so every targeted title that names it has to say the new one.
   *
   * THE TITLES BELONG TO WHOEVER OWNS THE TARGET. `writeTarget` routes a move into the branch
   * on screen, and a title reads "More than 25 m from <lat, lng>" — so resyncing the block's
   * set while editing branch 2 rewrote the wrong sentences and left branch 2's own stale. It
   * did not show before only because branches had no feedback to be stale.
   *
   * The label is derived from the data it is handed, so the branch case hands it the BRANCH's
   * target rather than the block's.
   */
  function pushTargetChange(overrides: Partial<GpsBlockData>) {
    const nextData = { ...currentDataRef.current, ...overrides };
    const branchId = activeBranch?.id;
    if (branchId) {
      const list = overrides.conditionalBranches ?? conditionalBranches ?? [];
      const next = list.map(b => (b.id !== branchId ? b : {
        ...b,
        feedback: {
          states: resyncGpsTargetedTitles(
            (b.feedback ?? feedback).states,
            { ...nextData, targetLat: b.targetLat, targetLng: b.targetLng },
            t, i18n.language),
        },
      }));
      setConditionalBranches(next);
      emitFields({ ...nextData, conditionalBranches: next });
      return;
    }
    const nextStates = resyncGpsTargetedTitles(feedback.states, nextData, t, i18n.language);
    const nextFeedback = { states: nextStates };
    setFeedback(nextFeedback);
    emitFields({ ...nextData, feedback: nextFeedback });
  }

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  const targetLatInputRef = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleGradingMode(mode: GradingMode) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(mode);
    push({ gradingMode: mode, feedback: { states: nextFeedbackStates } });
  }

  function handleCoordinateFormat(next: GPSCoordinateFormat) {
    setCoordinateFormat(next);
    push({ coordinateFormat: next });
  }

  function handleVisibilityMode(next: GPSVisibilityMode) {
    setVisibilityMode(next);
    push({ visibilityMode: next });
    // A reading staged under the old mode's rules can't survive the switch: the "capture was
    // pressed" flag means nothing in a mode with no button, and leaving it set would show a reading
    // in Live Reading that the teacher never took. Resetting also puts the test panel back to its
    // pre-reading state, which is what a mode change genuinely is.
    setTestTaken(false);
    setHasSubmitted(false);
  }

  /** The simulate strip's manual coordinate: one box per axis, applied the moment BOTH parse. Two
   * boxes rather than one free-text field is what removes the need for an apply button — there's no
   * "lat, lng" string left to parse, so there's no moment where the teacher has to tell us they've
   * finished typing. Each box is validated exactly like the target fields above it (same range, same
   * character cap); a box that doesn't parse just highlights and nothing arms. */
  const simManualLatNum = parseFloat(simManualLat);
  const simManualLngNum = parseFloat(simManualLng);
  const simManualLatBad = simManualLat.trim() !== '' && !(Number.isFinite(simManualLatNum) && Math.abs(simManualLatNum) <= 90);
  const simManualLngBad = simManualLng.trim() !== '' && !(Number.isFinite(simManualLngNum) && Math.abs(simManualLngNum) <= 180);
  function applyManualSim(lat: string, lng: string) {
    const la = parseFloat(lat), ln = parseFloat(lng);
    if (!isValidCoord(la, ln)) return;
    // Rounded to the same 6 decimals a real capture is, so a manual point and a device reading are
    // never distinguishable by spurious precision.
    handleTestSelectionChange({ kind: 'manual', lat: roundCoord(la), lng: roundCoord(ln) });
  }
  function handleSimManualLat(v: string) {
    const next = v;
    setSimManualLat(next);
    applyManualSim(next, simManualLng);
  }
  function handleSimManualLng(v: string) {
    const next = v;
    setSimManualLng(next);
    applyManualSim(simManualLat, next);
  }

  /** The reading the armed selection produces, gated by whether this visibility mode allows one to
   * exist yet. One helper for all three mount points (Preview, canvas card, phone Test tab) so they
   * cannot disagree about when a reading exists — the bug that gating per call site would invite. */
  /** What the armed selection WOULD report, with no visibility-mode gate. This is what the Simulate
   * hint describes: the hint is a PREDICTION ("Capture will report a point 12 m from the target"),
   * so withholding it until a capture had happened both contradicted its own wording and made the
   * strip grow a line the moment the button was pressed, shoving the widget below it down the page. */
  function simReadingFor(d: GpsBlockData): GpsSimReading | null {
    if (deviceArmed) {
      if (!deviceReading) return null;
      const lat = parseFloat(d.targetLat), lng = parseFloat(d.targetLng);
      return {
        ...deviceReading,
        // Measured against the authored target with the same haversine evaluateGPS uses, so the hint
        // and the evaluation can't disagree about how far away the teacher actually is.
        distanceMeters: isValidCoord(lat, lng)
          ? haversineDistanceMeters({ lat, lng }, { lat: deviceReading.lat, lng: deviceReading.lng })
          : 0,
        looksNetworkBased: false,
      };
    }
    return gpsSimReading(testSelection, d, simNonce);
  }
  /**
   * The reading the STUDENT surface may show, which Capture withholds until its button is pressed.
   *
   * In PREVIEW this comes from the task's one shared device rather than from this block's own
   * simulator — see `sensor-simulator-context`. A phone does not send one position to one block and
   * a different one to another, and two GPS blocks both reporting "Exact" against different targets
   * is precisely the impossibility a teacher needs Preview to reveal.
   *
   * The visibility gate still applies on top: Capture withholds a reading until its button is
   * pressed whatever is producing it, so the shared device changes WHERE the phone is, never WHEN
   * the block is allowed to have read it.
   */
  function readingFor(d: GpsBlockData): GpsSimReading | null {
    /**
     * A revisit shows what was captured, EXCEPT in Live.
     *
     * Live means the readout tracks the device, so putting a ten-minute-old fix back on screen
     * would be a lie about where the student is standing -- in a field experience they have
     * walked somewhere else since. Capture and Capture-on-Submit are the opposite: the stale
     * reading is exactly what they handed in, and it is replaced the next time they capture.
     *
     * Decided here rather than at the seed, because this is the one place the mode is in scope,
     * and the mode can be changed by a Flow effect mid-run.
     */
    if (d.visibilityMode !== 'live' && restoredReading) return restoredReading;
    if (!sensorHasReading(d.visibilityMode, testTaken)) return null;
    /*
     * PREVIEW reads the task's one shared device; the EDITOR reads this block's own strip.
     *
     * This used to be `sharedReadingFor(d) ?? simReadingFor(d)` unconditionally, on the stated
     * assumption that no provider sits above the editor. One does — `SensorSimulatorProvider`
     * wraps the whole task page, not just the preview surface — so the shared device answered
     * first everywhere, and since its heading starts at zero, every per-block box was dead: the
     * hint said "will report 212" off the typed value while the readout beside it stayed on 0 and
     * the legend graded against 0.
     *
     * Which strip is ON SCREEN is the thing that decides, and in the editor that is this block's.
     */
    return capturedReading ?? liveReadingFor(d);
  }

  /**
   * The receiver as it stands, with no capture gate in front of it.
   *
   * See `readingFor`'s own note for why the editor and Preview read different strips.
   */
  function liveReadingFor(d: GpsBlockData): GpsSimReading | null {
    return (isPreview ? sharedReadingFor(d) : null) ?? simReadingFor(d);
  }

  /**
   * The fix Capture froze, in the mode that has a Capture to press.
   *
   * GPS used to keep none: `testTaken` only decided WHETHER to show a coordinate, and the value
   * shown was still the live one, so a captured fix went on drifting under the student's eyes and
   * the word "captured" described nothing. Invisible in the editor, where the coordinate only
   * moves when the teacher moves the pin — and constant in a field, which is where it matters.
   * Live Reading keeps no snapshot by definition, and Capture on Submit takes its sample at
   * submission.
   */
  const [capturedReading, setCapturedReading] = useState<GpsSimReading | null>(null);

  /**
   * The reading as it stood at Submit, frozen.
   *
   * The sensor blocks keep no submitted snapshot of their own -- their result recomputes off the
   * live reading whenever it changes -- so without this there is nothing to record. An attempt
   * that logs a dead `touched: false` for a student whose GPS read perfectly well is worse than
   * no record at all.
   */
  const [submittedReading, setSubmittedReading] = useState<GpsSimReading | null>(null);
  useEffect(() => {
    if (isPreview && previewSubmitSignal) setSubmittedReading(readingFor(currentDataRef.current));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);
  useReportAnswer(hasSubmitted ? submittedReading : undefined);

  /**
   * This block's view of the shared device, or `null` when there is not one.
   *
   * Null covers both the editor (no provider above us) and the device tab, where the real sensor is
   * already the source and a simulated position would be the wrong answer to a different question.
   */
  function sharedReadingFor(d: GpsBlockData): GpsSimReading | null {
    if (!shared || shared.useDevice) return null;
    if (shared.signal === 'none') return null;
    const target = { lat: parseFloat(d.targetLat), lng: parseFloat(d.targetLng) };
    const { lat, lng } = shared.position;
    return {
      lat,
      lng,
      accuracy: accuracyForSignal(shared.signal).position,
      // Measured with the same haversine `evaluateGPS` uses, so the readout and the grade cannot
      // disagree about how far away the phone actually is.
      distanceMeters: isValidCoord(target.lat, target.lng)
        ? haversineDistanceMeters(target, { lat, lng })
        : null,
      looksNetworkBased: shared.signal === 'poor',
    };
  }
  /**
   * Whether the evaluation should be showing. `hasSubmitted` is the event-driven flag — Submit was
   * pressed, or continuous mode auto-submitted when a reading was taken. Capture is the only mode that
   * has a gesture to hang that on, so in continuous mode the other two evaluate immediately: Live
   * Reading is always reading, and Capture on Submit reads automatically. Without this both showed no
   * feedback whatever the teacher did, since neither has a control that could set the flag, and the
   * test bar only offers a Submit button while continuous is switched off.
   *
   * "Always reading" is not the same as HAS read, which is the `reading` argument. A live block that
   * has produced nothing yet has nothing to evaluate, and firing the catch-all on that emptiness put
   * "Your response has been recorded" on screen before the teacher had done a thing — true in the
   * sense that optional treats a blank as a response, and a plain lie as a sentence. A dead sensor
   * still evaluates with no reading: that IS the outcome, and the no-data card is what says so.
   */
  function evaluatedFor(d: GpsBlockData, reading: unknown): boolean {
    if (hasSubmitted) return true;
    if (!continuous || isPreview || d.visibilityMode === 'capture') return false;
    /*
     * LIVE READING: no authored answer, nothing fires.
     *
     * A live instrument is always reading, so a reading exists the instant the block does — the
     * simulator hands every new block a default one — and the card fired its feedback before the
     * teacher had touched anything. The honest gate is not "did somebody ask for this reading" but
     * "is there anything to test it against": a block with no answer authored has nothing for the
     * test surface to say, in any mode.
     *
     * Capture is unaffected, because it already needs a press, and Capture on Submit returns above
     * on `hasSubmitted`. So this lands on Live Reading, which is the only mode that evaluates
     * unprompted — and now the only default both sensors share.
     *
     * OPTIONAL is exempt, because there the premise does not hold: that mode authors no answer
     * by design, so "no answer yet" is permanent rather than a stage the block passes through,
     * and the rule would silence it for good. An optional sensor has nothing to set up and
     * nothing to wait for, so it reads continuously and says so.
     */
    if (d.gradingMode !== 'optional' && isGpsSetupIncomplete(d)) return false;
    return reading != null || sensorDead;
  }


  const simManualProps = {
    lat: simManualLat, lng: simManualLng,
    onLatChange: handleSimManualLat, onLngChange: handleSimManualLng,
    latInvalid: simManualLatBad, lngInvalid: simManualLngBad,
  };

  function handleCapture() {
    setIsCapturing(true);
    setCaptureError(null);
    const startedAt = performance.now();
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setIsCapturing(false);
        setPendingCapture({
          lat: roundCoord(pos.coords.latitude), lng: roundCoord(pos.coords.longitude), accuracy: pos.coords.accuracy,
          altitude: pos.coords.altitude, elapsedMs: performance.now() - startedAt,
        });
      },
      (err) => {
        setIsCapturing(false);
        const key = err.code === 1 ? 'captureErrorPermissionDenied'
          : err.code === 2 ? 'captureErrorPositionUnavailable'
          : err.code === 3 ? 'captureErrorTimeout'
          : 'captureErrorGeneric';
        setCaptureError(t(`blocks.gps.${key}`));
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  function handleUsePendingCapture() {
    if (!pendingCapture) return;
    const lat = String(pendingCapture.lat);
    const lng = String(pendingCapture.lng);
    setTargetLat(lat);
    setTargetLng(lng);
    writeTarget({ targetLat: lat, targetLng: lng });
    setPendingCapture(null);
    requestAnimationFrame(() => targetLatInputRef.current?.focus());
  }

  // Unparseable text simply doesn't apply — no toast, no issue-bar entry, no message anywhere.
  // The field's own persistent red border (see pasteInvalid at its render site) already says the
  // content can't be used, which is the whole story: this is a scratch convenience input, not part
  // of the block's saved setup, so a failed paste isn't a defect worth its own error mode.
  function handleApplyPaste() {
    const result = parsePastedLocation(pasteInput);
    if ('errorKey' in result) return;
    // Same cap as a device capture — a pasted Maps URL carries just as many meaningless digits.
    const lat = String(roundCoord(result.coord.lat));
    const lng = String(roundCoord(result.coord.lng));
    setPasteInput('');
    writeTarget({ targetLat: lat, targetLng: lng });
  }

  // No sanitising here — NumberField's `decimal` grammar already did it, and doing it twice
  // would strip a comma a pt-BR teacher legitimately typed as their decimal separator.
  function handleManualLat(v: string) { writeTarget({ targetLat: v }); }
  function handleManualLng(v: string) { writeTarget({ targetLng: v }); }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = activeFeedbackStates.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...defaults.filter(s => s.trigger === 'poor_accuracy'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /**
   * A new targeted state starts BLANK, and at `=`.
   *
   * It used to invent one: `lt:10`, then `lt:20`, counting up to dodge a collision with a trigger
   * already in the list. Every one of those is a real, gradeable condition the teacher never
   * authored, presented as though it were a suggestion. The dedupe loop went with the numbers — it
   * existed only to keep them distinct, and two unfinished conditions do not collide in any way
   * that matters, since states are keyed by `id`.
   */
  /** The card the Add button last produced, so it opens ready to type into — the owner's
   *  preference for this family, and the reason `startExpanded` exists. Keyed by mode, because
   *  the panel reorders its sections on a switch and the cards remount; a bare id would reopen
   *  the last one every time. State, not a ref: it is read during render. */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    const trigger = 'exact:';
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: gpsTargetedTitle(trigger, currentDataRef.current, t, i18n.language),
      trigger, content: '',
      evaluation: currentDataRef.current.gradingMode === 'graded' ? 'incorrect' : 'neutral',
    };
    const next = { states: rebuildStates([...targeteds, newState]) };
    setFeedback(next);
    emitFields({ ...currentDataRef.current, feedback: next });
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    const next = { states: rebuildStates(arrayMove(targeteds, from, to)) };
    setFeedback(next);
    emitFields({ ...currentDataRef.current, feedback: next });
  }

  function handleDone() {
    push();
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // Genuinely interactive now, not a disabled snapshot — same widget and same evaluateGPS path
    // the live test panel uses, triggered by the page-level bottom Submit so every block on the
    // page evaluates together. The reading is scenario-simulated rather than sensor-read: a
    // teacher previews on whatever device they're authoring on, which usually has no usable GPS,
    // and a permission failure there would tell them nothing about their block.
    // The version THIS student drew, from the task roll — see `versionRollKey`.
    /* Preview takes the drawn version from the session roll, like every other block. GPS reads
       its variable roll through `numericRoll` rather than the task-preview hook, so the version
       is looked up directly from the same roll map. */
    const d = buildNormalizedGpsData(initData(block.data), t, previewVersionId);
    const previewReading = readingFor(d);
    const previewHintReading = simReadingFor(d);
    const previewTest = computeGpsTestData(d, t, i18n.language, previewReading, evaluatedFor(d, previewReading), numericRoll, sensorDead);
    /*
     * THE SAME DERIVATION ASKED ABOUT THE DRAFT -- reported, never rendered.
     *
     * `previewTest` answers for the SUBMITTED reading, so before a capture nothing is active and
     * Behind the Scenes had nothing to show for Current. Passing `hasSubmitted` as true asks
     * "what would fire if they submitted now", which for an uncaptured receiver is No Answer --
     * which is the honest answer, and the one the wired blocks already give.
     */
    const draftTest = computeGpsTestData(d, t, i18n.language, previewReading, true, numericRoll, sensorDead);
    return (
      // No question render here — GpsAnswerDisplay owns it, so Preview cannot drift from the canvas
      // card or the phone Test tab the way it previously had.
      <div className='flex flex-col gap-3'>
        <GpsTestWidget
          scenario={scenario.ghostedScenario}
          d={d}
          // Preview reads the task's one device, so this block's own strip stays hidden. In
          // the editor there is no provider above us and this is false, which is what keeps
          // per-block testing exactly as it was.
          sharedDevice={!!shared}
          selection={testSelection}
          mode={sourceMode}
          onModeChange={setSourceMode}
          deviceStatusHint={deviceStatusHint}
          deviceNotice={studentDeviceNotice}
          onSelectionChange={handleTestSelectionChange}
          manual={simManualProps}
          reading={previewReading}
          hintReading={previewHintReading}
          onCapture={handleTestCapture}
          onClearReading={handleClearCapture}
          liveReading={liveReadingFor(d)}
          hasReading={previewReading != null}
          t={t}
          locale={i18n.language}
        >
          {/* See keyword-block: only the block knows which conditions are in force. */}
          <ReportTestChips chips={draftTest.testChips} states={draftTest.testResult} />
          {renderGpsFiredFeedback(previewTest.testResult)}
        </GpsTestWidget>
      </div>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    // Through the version in force, so the card tests the target the pager is pointed at.
    const d = buildNormalizedGpsData(initData(block.data), t, shownVersionId);
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    const collapsedIssues = settleStored(memoizedByKey(
      gpsIssuesCache,
      block.data,
      // Keyed on the shown version too: the target itself is per version.
      [t, i18n.language, siblingGpsTargets, shownVersionId],
      () => gpsIssues(d, { ...issueCtx, siblingGpsTargets }),
    ));
    const collapsedReading = readingFor(d);
    const collapsedHintReading = simReadingFor(d);
    const collapsedTest = computeGpsTestData(d, t, i18n.language, collapsedReading, evaluatedFor(d, collapsedReading), numericRoll, sensorDead);
    const gpsAriaLabel = d.question
      ? t('blocks.gps.editAriaBaseWithQuestion', { question: d.question.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) })
      : t('blocks.gps.editAriaBase');

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<MapPin className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.gps.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={gpsAriaLabel}
        answerBarSlot={() => gpsCollapsedBar(d, t, i18n.language, collapsedIssues)}
        testEvaluation={collapsedTest.testResult ? (collapsedTest.testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: evaluatedFor(d, collapsedReading),
          chips: collapsedTest.testChips,
          onReset: handleTestReset,
          // Reroll redraws the randomised value INSIDE the armed scenario — "another just-inside-
          // tolerance reading", not another scenario. It lives here rather than on the scenario control
          // because a <select> doesn't fire onChange when you re-pick what's already selected, and
          // because this is the same button that rerolls variables in every other block.
          onReroll: handleSimReroll,
          // Which branch the equation selected for this run. Without it a teacher testing a branched
          // block sees a target they did not choose and no indication of why.
          /* One row for everything this test resolved to — shared with the phone bar and every
             other banked block. GPS reports WHICH branch matched but not the equation's value, so
             the condition detail is omitted here and the row reads "Branch 1 of 2" as before. */
          topNotices: (
            <VersionBranchNotice
              versions={scenario.testPager?.versions ?? shownVersions}
              activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
              onSelectVersion={scenario.testPager?.onSelect ?? setTestVersion}
              noun={scenario.testPager?.noun ?? 'version'}
              editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={collapsedTest.matchedBranchIndex >= 0 ? {
                index: collapsedTest.matchedBranchIndex,
                total: (d.conditionalBranches ?? []).length,
              } : null}
              onEdit={() => {
                if (collapsedTest.matchedBranchIndex >= 0) requestBranchJump(block.id, collapsedTest.matchedBranchIndex);
                editShownVersion((d.conditionalBranches ?? [])[collapsedTest.matchedBranchIndex]?.id);
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={collapsedIssues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {/* DISPLAY STATE ONLY. It reserves the space the question will fill and reads as a normal
            authoring placeholder here, but a student must never see placeholder text — so it is gated on
            !isSelected, because a selected card IS the live test surface. The blank-question warning
            itself lives on the issue bar; this is just the reserved slot. */}
        {!isSelected && !d.question && (
          <EmptyQuestionNote />
        )}
        {isSelected ? (
          // Block-test roll only. Preview is deliberately NOT wrapped: it inherits the
          // task-level provider so every block on the page resolves against one shared roll.
          <BlockPreviewProvider value={rolledValues}>
          <GpsTestWidget
          scenario={scenario.ghostedScenario}
            d={d}
            selection={testSelection}
            mode={sourceMode}
            onModeChange={setSourceMode}
            deviceStatusHint={deviceStatusHint}
            deviceNotice={studentDeviceNotice}
            onSelectionChange={handleTestSelectionChange}
            manual={simManualProps}
            reading={collapsedReading}
            hintReading={collapsedHintReading}
            onCapture={handleTestCapture}
            onClearReading={handleClearCapture}
            liveReading={liveReadingFor(d)}
            hasReading={collapsedReading != null}
            t={t}
            locale={i18n.language}
          >
            {renderGpsFiredFeedback(collapsedTest.testResult)}
          </GpsTestWidget>
          </BlockPreviewProvider>
        ) : (
          /* The unselected card is the SAME student widget, rendered disabled — not a hand-built
             lookalike. It used to be one, and that copy drifted twice (it kept drawing a capture
             button for Live Reading blocks) before this shared it. `disabled` makes it non-interactive
             so a click still bubbles to the card's own onActivate rather than being swallowed by a
             real button, which is how every other block's collapsed preview behaves.
             In Live Reading the authored target stands in for a fix; Capture shows its button
             unpressed; Capture on Submit shows nothing at all, which is exactly what a student gets.
             The actual correct-answer readout lives in the answer bar (gpsCollapsedBar), not here, so
             this card never needs a map. */
          <GpsAnswerDisplay
          scenario={scenario.ghostedScenario}
            disabled
            d={d}
            reading={d.visibilityMode === 'live' ? targetAsReading(d) : null}
            hasReading={d.visibilityMode === 'live'}
            t={t}
            locale={i18n.language}
          />
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Validation / derived state ──────────────────────────────────────────────

  const targetLatNum = parseFloat(targetLat);
  const targetLngNum = parseFloat(targetLng);
  const toleranceNum = parseFloat(toleranceRadius);
  const accuracyCutoffNum = parseFloat(accuracyCutoff);

  // Purely visual, deliberately NOT a BlockIssue: the paste box is a scratch input that never
  // reaches the saved block, so bad text in it is not a setup defect and must not gate publish.
  const pasteInvalid = pasteInput.trim() !== '' && 'errorKey' in parsePastedLocation(pasteInput);
  const latInvalid = targetLat.trim() !== '' && !(Number.isFinite(targetLatNum) && Math.abs(targetLatNum) <= 90);
  const lngInvalid = targetLng.trim() !== '' && !(Number.isFinite(targetLngNum) && Math.abs(targetLngNum) <= 180);
  const toleranceInvalid = toleranceRadius.trim() !== ''
    && !(Number.isFinite(toleranceNum) && toleranceNum > 0 && toleranceNum <= MAX_TOLERANCE_METERS);

  // Checked against the staged pending capture, not the committed target — the whole point of
  // the holding-box/"Use" step is to warn *before* a poor-accuracy reading gets committed.
  // accuracyCutoff is always one of the three preset values now (see GPS_ACCURACY_PRESETS), so
  // there's no "invalid" state left to guard against here.
  const pendingCaptureAccuracyPoor = !!pendingCapture && pendingCapture.accuracy > accuracyCutoffNum;

  // Soft heuristic, not a hard gate — the Geolocation API never reveals whether a fix came from
  // real satellite GPS or a WiFi/IP-based network lookup (deliberately, to avoid exposing a
  // hardware fingerprinting signal), and a network-based fix can self-report a deceptively small
  // `accuracy` value while still being wrong at country scale. A response this fast (real GPS
  // acquisition, even warm-started, generally takes longer) combined with no altitude (network
  // lookups essentially never return one; real GPS often, not always, does) is the closest
  // available proxy — neither condition alone nor both together is conclusive, so this only ever
  // adds a second warning, never blocks "Use" the way the accuracy-cutoff check conceptually might.
  const pendingCaptureLooksNetworkBased = !!pendingCapture && pendingCapture.elapsedMs < 1000 && pendingCapture.altitude == null;

  // Live editor-state issues — computed from what the teacher is currently typing, not from the
  // last-pushed block.data, so the bar and the field highlights update on the same keystroke.
  // Same role as MCQ/Ordering's own `editorIssues`/`liveD`. Deliberately unmemoized: it reads
  // component state that changes on every keystroke anyway, so a cache keyed on block.data would
  // return stale results.
  const liveD: GpsBlockData = {
    question: questionHtmlRef.current,
    targetLat, targetLng, toleranceRadius, showTolerance, accuracyCutoff, visibilityMode, coordinateFormat, gradingMode,
    feedback,
  };
  const editorIssues = settleLive(gpsIssues(liveD, { ...issueCtx, siblingGpsTargets }));

  // ── Phone "Test" tab ────────────────────────────────────────────────────────
  const phoneReading = isPhone ? readingFor(liveD) : null;
  const phoneHintReading = isPhone ? simReadingFor(liveD) : null;
  const phoneTest = isPhone ? computeGpsTestData(liveD, t, i18n.language, phoneReading, evaluatedFor(liveD, phoneReading), numericRoll, sensorDead) : null;
  const phoneTestSlot = phoneTest ? (
    // Block-test roll only. Preview is deliberately NOT wrapped: it inherits the
    // task-level provider so every block on the page resolves against one shared roll.
    <BlockPreviewProvider value={rolledValues}>
    <GpsTestWidget
          scenario={scenario.ghostedScenario}
      d={liveD}
      selection={testSelection}
      mode={sourceMode}
      onModeChange={setSourceMode}
      deviceStatusHint={deviceStatusHint}
      deviceNotice={studentDeviceNotice}
      onSelectionChange={handleTestSelectionChange}
      manual={simManualProps}
      reading={phoneReading}
      hintReading={phoneHintReading}
      onCapture={handleTestCapture}
      onClearReading={handleClearCapture}
      liveReading={liveReadingFor(liveD)}
      hasReading={phoneReading != null}
      t={t}
      locale={i18n.language}
    >
      {renderGpsFiredFeedback(phoneTest.testResult)}
    </GpsTestWidget>
    </BlockPreviewProvider>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: evaluatedFor(liveD, phoneReading),
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: handleSimReroll,
    // Which branch the equation selected for this run. Without it a teacher testing a branched
    // block sees a target they did not choose and no indication of why.
    /* The same row as the canvas card's — one component, so the two can never drift. */
    topNotices: (
      <VersionBranchNotice
        versions={scenario.testPager?.versions ?? shownVersions}
        activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
        /* The EDITOR's pointer here: on a phone, Edit and Test are two tabs over ONE session. */
        onSelectVersion={scenario.testPager?.onSelect ?? selectVersion}
        noun={scenario.testPager?.noun ?? 'version'}
        editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
        stacked
        branch={phoneTest.matchedBranchIndex >= 0 ? {
          index: phoneTest.matchedBranchIndex,
          total: (liveD.conditionalBranches ?? []).length,
        } : null}
        onEdit={() => {
          if (phoneTest.matchedBranchIndex >= 0) setActiveBranchIndex(phoneTest.matchedBranchIndex);
          editShownVersion((liveD.conditionalBranches ?? [])[phoneTest.matchedBranchIndex]?.id);
          setPhoneTestMode('edit');
        }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  // Whether Correct currently has a usable, positive threshold to compare against — distinct
  // from toleranceInvalid above, which only flags *malformed* (non-blank, non-positive)
  // content; a blank field is equally "not usable" here, since e.g. `distance <= NaN` is
  // always false in JS, meaning Correct can never fire either way but for a different reason
  // than a bad number would. accuracyCutoff has no equivalent "not usable" state anymore — the
  // dropdown always yields one of the three preset values.
  const toleranceValid = Number.isFinite(toleranceNum) && toleranceNum > 0;
  const acceptedRange: GPSInterval | null = toleranceValid ? { lo: 0, hi: toleranceNum } : null;

  // Computed from the raw feedback list directly (not the later `targetedStates`/
  // `targetedTriggers` used by the targeted-feedback UI below) so this warning helper can be
  // defined once, ahead of every slot that needs it — noAnswerSlot/partialAnswerSlot included.
  const allTargetedTriggers = activeFeedbackStates.filter(s => s.type === 'targeted').map(s => s.trigger);
  const overlappingTriggerPairs = findGpsOverlappingFeedbackPairs(allTargetedTriggers);
  const overlappingTriggerSet = new Set(overlappingTriggerPairs.flat());

  const acceptedClaimed = gradingMode === 'graded'
    || activeFeedbackStates.some(x => x.type === 'targeted' && x.trigger === SET_ANSWER_TRIGGER);
  function gpsFeedbackWarning(trigger: string): string | undefined {
    /*
     * Both verdicts are about the ACCEPTED BAND, so they need the band to be spoken for.
     *
     * Graded always speaks for it: Correct is checked first and is terminal, so a target inside it
     * never fires. Outside graded only a Set Answer target claims it — and with no claimant there
     * is nothing to overlap, because the catch-all is catching those readings anyway. The issues
     * list has made this check graded-only for a while; these per-card copies never did, so a
     * Participation block warned "Completely overlaps Correct" about a Correct it does not have.
     *
     * Set Answer authored is the case the warning is genuinely FOR outside graded, and it is now
     * the case that reaches it.
     */
    /*
     * BOTH SIDES OF A COLLISION ARE LIT, which is what makes one findable. A value-vs-value pair
     * already did — `overlappingTriggerSet` is built from `.flat()` — and Set Answer was the
     * one-sided case: it states the band rather than overlapping it, which is true and is not the
     * point. A teacher looking at one lit card should not have to hunt for what it collides with.
     */
    if (trigger === SET_ANSWER_TRIGGER) {
      return acceptedClaimed && activeFeedbackStates.some(x => x.type === 'targeted' && x.trigger !== SET_ANSWER_TRIGGER
          && (isGpsFeedbackFullySubsumed(x.trigger, acceptedRange) || doesGpsFeedbackOverlapAccepted(x.trigger, acceptedRange)))
        ? t('blocks.gps.feedbackOverlapWarning') : undefined;
    }
    if (!acceptedClaimed) return overlappingTriggerSet.has(trigger) ? t('blocks.gps.feedbackOverlapWarning') : undefined;
    if (isGpsFeedbackFullySubsumed(trigger, acceptedRange)) return t('blocks.gps.feedbackSubsumedWarning');
    if (doesGpsFeedbackOverlapAccepted(trigger, acceptedRange)) return t('blocks.gps.feedbackOverlapsCorrectWarning');
    if (overlappingTriggerSet.has(trigger)) return t('blocks.gps.feedbackOverlapWarning');
    return undefined;
  }

  // ── Feedback slots ──────────────────────────────────────────────────────────

  const unansweredState = activeFeedbackStates.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  /* The sensor states, in the order evaluateGPS gates them — and they sit on either side of No
     Answer in the panel because that is where they resolve. */
  const noSensorDataState = activeFeedbackStates.find(s => s.trigger === NO_SENSOR_DATA_TRIGGER);
  const poorAccuracyState = activeFeedbackStates.find(s => s.trigger === 'poor_accuracy');
  const noSensorDataCards = noSensorDataState ? [
    <SensorFeedbackCard key='no-data' state={noSensorDataState} getTitle={getTitle}
      label={t('blocks.sensors.noSensorDataFeedback')}
      onChange={patch => updateActiveFeedbackState(noSensorDataState.id, patch)}
      activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />,
  ] : [];
  const poorSensorCards = poorAccuracyState ? [
    <SensorFeedbackCard key='poor' state={poorAccuracyState} getTitle={getTitle}
      label={t('blocks.gps.feedbackTriggerPoorAccuracy', { cutoff: fmtLocale(accuracyCutoffNum, i18n.language) })}
      onChange={patch => updateActiveFeedbackState(poorAccuracyState.id, patch)}
      activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />,
  ] : [];

  // Mode-scoped: a NAMED trigger authored in one mode is hidden, not deleted, in the modes it
  // cannot fire in — see `targetedFiresIn`. Value triggers fire in every mode and all stay.
  const targetedStates = visibleTargeted(activeFeedbackStates, gradingMode);
  const canAddMore = targetedStates.length < 5;

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, targetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={targetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {targetedStates.map(s => {
            const ORDER = ['neutral', 'incorrect'] as const;
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                titleLocked
                /* Neutral and locked outside graded: the stored evaluation mirrors a verdict
                   the mode has not got, and there is no incorrect to cycle TO. */
                title={s.title} content={s.content}
                evaluation={displayEvaluation(gradingMode, s.evaluation)}
                evalLocked={gradingMode !== 'graded'}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                warning={gpsFeedbackWarning(s.trigger)}
                triggerZone={<GpsTriggerEditor answerTaken={targetedStates.some(x => x.id !== s.id && x.trigger === SET_ANSWER_TRIGGER)} trigger={s.trigger} onChange={trigger => updateActiveFeedbackState(s.id, { trigger, title: gpsTargetedTitle(trigger, liveD, t, i18n.language) })} t={t} onPlainFieldFocus={disconnectSharedToolbar} gradingMode={gradingMode} answerConditionChips={gpsChipRow('correct')} />}
                /* The preset states the authored CONDITION, resolved against the roll, exactly
                   as graded's Correct card does — "= 90°", not the word "Answer". Same chips,
                   same derivation, so a teacher reads one sentence whichever way it was made. */
                triggerZoneSummary={s.trigger === SET_ANSWER_TRIGGER
                  ? gpsChipRow('correct')
                  : <GpsTriggerChip trigger={s.trigger} evaluation={displayEvaluation(gradingMode, s.evaluation)} t={t} d={liveD} locale={i18n.language} />}
                onTitleChange={() => {}}
                onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                onDelete={() => deleteActiveFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  // Correct genuinely cannot fire without BOTH a real target coordinate and a usable tolerance
  // radius — toleranceValid alone used to gate this, which missed the case of a valid
  // tolerance but no target lat/lng set at all (targetLat/targetLng blank parse to NaN, so the
  // haversine distance check can never be true either way, but the UI still showed the
  // tolerance chip as if a real Correct condition existed).
  //
  // Asked through the same function the test bar, the issue list and the card all ask, rather than
  // restated here as `toleranceValid && targetConfigured`. The two said the same thing today; they
  // were two places to keep saying it.
  const correctConfigured = !isGpsSetupIncomplete(liveD);
  const gpsPositiveInactive = gradingMode === 'graded' && !correctConfigured;
  /**
   * FUNCTION DECLARATIONS, not consts, and that is the whole point.
   *
   * The slots below are built with `.map()`, which runs immediately — so a `const` holding one of
   * these rows is read while that array is built, and a `const` declared after the slot sits in its
   * temporal dead zone and throws ReferenceError on the first render of a newly added block. That
   * is exactly what shipped for a few minutes here. Neither the type checker nor a unit test on
   * `gpsChips` can see it, and `no-use-before-define` reports 31 lexical-position sites app-wide,
   * most of them harmless, so it is not a rule that can be switched on to catch this.
   *
   * Hoisted declarations make the ordering irrelevant instead of merely correct today.
   */
  function chipRow(chips: ConditionChips) {
    return <ConditionChipRow chips={chips} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />;
  }
  function gpsChipRow(kind: 'correct' | 'incorrect') {
    // The mode-check this used to write out by hand lives in `gpsChips` now, with every other
    // block's: these are graded's own condition chips, reused by the preset and the catch-all,
    // and they arrive green with a tick unless the derivation is told otherwise.
    return chipRow(gpsChips(kind, liveD, t, i18n.language, gradingMode));
  }

  /**
   * The catch-all names what it still catches, like Default Incorrect — but only once SET ANSWER
   * carves the accepted band out of it. Without one the catch-all genuinely does catch the whole
   * line including the answer, and the gap derivation (which always excludes the band) would
   * claim otherwise. So: enumerate when the band is spoken for, say the plain label when it is
   * not. The tone follows ANY target, not just Set Answer — same rule as everywhere else.
   */
  const gpsSetAnswerAuthored = targetedStates.some(x => x.trigger === SET_ANSWER_TRIGGER);
  const gpsParticipationCatchAll = gradingMode === 'graded' ? undefined : chipRow(catchAllChips(
    gradingMode, targetedStates.length > 0, t,
    // Enumerates whenever ANYTHING is targeted. It used to fall back to a bare "Any Answer"
    // unless Set Answer happened to be one of the targets, so a teacher who had carved the line
    // up with "more than 40 m" was told the card still accepted everything.
    () => gpsChips('incorrect', liveD, t, i18n.language, gradingMode, gpsSetAnswerAuthored)));

  const correctSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={gpsPositiveInactive}
        // Graded reads its condition from `gpsChips`, the same derivation the test legend draws, so
        // the two cannot disagree about a missing radius or an unreachable state. Survey/Optional
        // name the MODE rather than a distance, and there is no chip for that.
        triggerZone={gradingMode === 'graded' ? gpsChipRow('correct') : gpsParticipationCatchAll}
        triggerZoneSummary={gradingMode === 'graded' ? gpsChipRow('correct') : gpsParticipationCatchAll}
        title={catchAllTitle(gradingMode, targetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /* The tick goes once a target exists — the catch-all must not affirm an answer the
           teacher ignored more strongly than one they wrote for. Same rule as every other block. */
        evaluation={gradingMode !== 'graded' && targetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const targetedTriggers = targetedStates.map(s => s.trigger);
  const defaultIncorrectUnreachable = isGpsDefaultIncorrectUnreachable(acceptedRange, targetedTriggers);

  const incorrectSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={defaultIncorrectUnreachable}
        // Same derivation the test legend draws — `gpsChips` carries both the canonical "beyond X"
        // sentence and the per-gap fallback for a cleared radius, so the two surfaces state the
        // catch-all identically instead of each choosing when to fall back.
        triggerZone={gpsChipRow('incorrect')}
        triggerZoneSummary={gpsChipRow('incorrect')}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Editing shell ───────────────────────────────────────────────────────────

  // One definition, rendered either inside the branch carousel or on its own — the six content
  // blocks hoist their per-branch payload the same way (optionListJsx, pointListJsx) rather than
  // writing the ternary's two arms out twice.
  const targetFieldsJsx = (
    <div className='grid grid-cols-2 gap-2'>
      <div className='flex flex-col'>
        <label htmlFor='targetLat' className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.gps.fieldTargetLat')}</label>
        {/* aria-invalid alone, no aria-describedby — the message now lives on the shared
            issue bar, not in an inline paragraph this field could point at. */}
        <NumberField
          grammar='decimal'
          allowNegative
          value={shownLat}
          onChange={handleManualLat}
          hasError={latInvalid}
          maxLength={COORD_MAX_CHARS}
          fieldId='targetLat'
          ariaLabel={t('blocks.gps.fieldTargetLat')}
          onFocus={disconnectSharedToolbar}
          className='w-full' />
      </div>
      <div className='flex flex-col'>
        <label htmlFor='targetLng' className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.gps.fieldTargetLng')}</label>
        <NumberField
          grammar='decimal'
          allowNegative
          value={shownLng}
          onChange={handleManualLng}
          hasError={lngInvalid}
          maxLength={COORD_MAX_CHARS}
          fieldId='targetLng'
          ariaLabel={t('blocks.gps.fieldTargetLng')}
          onFocus={disconnectSharedToolbar}
          className='w-full' />
      </div>
    </div>
  );

  return (
    <>
    <InputBlockFrame
      blockId={block.id}
      fullScreen={isPhone && !embedded}
      // One-off action failures, per the rule that a Toast covers "errors when capturing data"
      // while the issue bar covers persistent setup defects. Anchored to this panel rather than
      // the viewport so the message lands beside the field that raised it — a capture/paste
      // failure otherwise appeared at the bottom of the entire task.
      toastSlot={
        <>
          {captureError && <Toast anchored message={captureError} type='error' onDismiss={() => setCaptureError(null)} />}
        </>
      }
      landscapeLayout={isPhoneLandscape}
      icon={<MapPin className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.gps.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.gps.closeEditor')}
      onClose={handleDone}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        // House style, matching Slider's (the widest multi-section panel): a FLAT sequence of siblings
        // in the flyout's own `flex flex-col gap-3.5`, one uppercase section heading, `mt-3` to open a
        // new group, and NO dividing lines or wrapper divs — 7 of the 10 panels have no border-t at all.
        // No `mb` on the heading either: the container gap already spaces it, so a margin was added to
        // that gap and left a visible hole underneath.
        // Everything here is student display, so there is ONE category. The two choices under it get
        // muted sub-labels rather than a second uppercase heading, which would read as a second
        // category; no other panel has needed two radio groups inside one section before, so this
        // sub-label is the one new piece of vocabulary.
        <>
          {/* Capture Mode decides whether the student takes a reading and when — how the answer is
              GIVEN, not how the block looks — so it heads its own Answer Format section, matching
              Compass. Coordinate Format below is presentation and stays under Student Display. */}
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>

            <RadioGroup fieldId='visibilityMode'
              aria-label={t('blocks.captureMode.label')}
              value={visibilityMode}
              onChange={v => handleVisibilityMode(v as GPSVisibilityMode)}
              options={[
                { value: 'live', label: t('blocks.captureMode.live') },
                { value: 'capture', label: t('blocks.captureMode.capture') },
                { value: 'passive', label: t('blocks.captureMode.onSubmit') },
              ]}
            />
          </OptionsSection>

          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>

            {/* A one-line switch sitting under BOTH reading modes, not nested inside either: a coordinate
                is shown in Live Reading and in Capture alike, so the format belongs to the block, not to a
                mode. A SegmentedControl rather than a second labelled radio list because this is a
                two-way inline choice — exactly what that control is for, it's what Slider and Compass
                already use in their own panels, and it removes a sub-heading and four lines of height.
                The visible text is the example itself (the shortest thing that actually shows the
                difference: a signed number vs a hemisphere letter); the full descriptive string stays as
                each segment's aria-label. Disabled, not hidden, in Capture on Submit — nothing is
                displayed there, and options grey out rather than disappear. */}
            <CheckboxRow fieldId='showTolerance'
              checked={showTolerance}
              onToggle={() => { const v = !showTolerance; setShowTolerance(v); push({ showTolerance: v }); }}
              label={t('blocks.gps.showTolerance')}
              // The tolerance it reveals is the accepted RADIUS, which only graded has. Disabled
              // rather than hidden: a lone control in a section that still has other work.
              disabled={!settingAvailable('showTolerance', gradingMode)}
            />

            <SegmentedControl fieldId='coordinateFormat'
              aria-label={t('blocks.gps.coordinateFormatGroupLabel')}
              stretch
              disabled={visibilityMode === 'passive'}
              value={coordinateFormat}
              onChange={v => handleCoordinateFormat(v as GPSCoordinateFormat)}
              options={[
                { id: 'decimal', label: t('blocks.gps.coordinateFormatDecimalShort'), ariaLabel: t('blocks.gps.coordinateFormatDecimal') },
                { id: 'cardinal', label: t('blocks.gps.coordinateFormatCardinalShort'), ariaLabel: t('blocks.gps.coordinateFormatCardinal') },
              ]}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.gps.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      feedbackPanelProps={{
        noAnswerSlot,
        noSensorDataSlot: <>{noSensorDataCards}</>,
        poorSensorSection: poorSensorCards.length > 0
          ? {
            // Named only when there is more than one, so the heading says what they share rather
            // than repeating a lone card's own label back at it.
            heading: poorSensorCards.length > 1 ? t('blocks.sensors.poorSectionHeading') : undefined,
            content: <>{poorSensorCards}</>,
          }
          : undefined,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          isEmpty: targetedStates.length === 0,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
          {/* The three-way offer, when linking meets an existing bank — §7.1. */}
          {scenario.remapModal}
          {/* The link control and what it points at, for BOTH kinds of link — see
              `VariesWithSection`, which decides on its own whether a DRIVEN block
              needs it. This was five lines of identical JSX in twelve blocks. */}
          <VariesWithSection
            following={!!scenario.activeScenario}
            linkMenu={scenario.linkMenu}
            stimulus={scenario.editorScenario}
          />
          {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
              the answer setup, because a driven axis varies the question too. Renders its
              children bare when this block is not driven. See `DrivenRowFrame`. */}
          <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
          {/* A bank is a niche feature: in the heading it is available without being advertised. */}
          <EditorSectionHeading
            label={t('blocks.areas.question')}
            divider={!!scenario.activeScenario}
            action={(
              <div className='flex items-center gap-2'>
              {/* The bank is GONE while this block follows a scenario, not merely inert (§2.2). */}
              {!scenario.following && (
              <VersionSelector
                versions={(initData(block.data).versions ?? []) as { id: string }[]}
                activeId={editorVersionId}
                onSelect={selectVersion}
                onAdd={() => {
                  const next = addGPSVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                  emit(versionStructure(block.data, next));
                  const added = next.versions?.[next.versions.length - 1];
                  if (added) selectVersion(added.id, next);
                }}
                onRemove={(id) => {
                  const next = removeGPSVersion(initData(block.data), id);
                  emit(versionStructure(block.data, next));
                  const landing = next.versions?.[0];
                  selectVersion(landing?.id, next);
                  setTestVersion(landing?.id);
                }}
              />
              )}
              {/* Silent once there is a Varies-With section, which is where the
                  control moves to — see `QuestionHeadingLink`. Written as
                  `!activeScenario` it knew about scenarios only, so a DRIVEN block
                  showed the link twice and the heading overflowed sideways. */}
              <QuestionHeadingLink
                following={!!scenario.activeScenario}
                linkMenu={scenario.linkMenu}
              />
              </div>
            )}
          />
          <BlockQuestionEditor
            value={questionHtml}
            onChange={html => {
              // The render-time SNAPSHOT has to move with the ref, not just the ref.
              // `currentDataRef.current` is rebuilt during render, and this handler runs before any
              // re-render, so every push path would otherwise spread the PREVIOUS question and leave
              // the persisted value one keystroke behind what is on screen. MCQ/Dropdown/Keyword avoid
              // this by reading questionHtmlRef live at push time; these blocks bake the question into
              // `fields` instead, so the two have to be kept in lockstep here.
              questionHtmlRef.current = html;
              setQuestionHtml(html);
              currentDataRef.current = { ...currentDataRef.current, question: html };
              push();
            }}
            toolbar={sharedToolbar}
            editorRef={questionEditorRef}
            shouldFocusRef={shouldFocusEditor}
            embedded={embedded}
          />

          {/*
            * ANSWER SETUP, hidden in OPTIONAL only.
            *
            * Optional is the one mode with no use for an answer key. Survey keeps it: Set Answer
            * targets the authored band, so the band is live there even though nothing is graded. So the target coordinate, its radius, and the
            * capture and paste controls that fill them have nothing to act on. Hidden rather than
            * greyed, because the whole SECTION's job is gone — the same call the feedback panel
            * already makes for its correctness cards in optional. A single control that merely has
            * no effect is disabled in place instead.
            *
            * Nothing is deleted: switch back to Has Correct and the target is as it was.
            */}
          {gradingMode !== 'optional' && (<>
            <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

            {/* Offered only once the QUESTION holds a variable chip, and only in graded mode — the
                same gate every other branching block uses. A location cannot be rolled, so branching
                is how GPS varies an answer per student: pick one of N real places by equation. */}
            {htmlHasVariableChips(questionHtmlRef.current) && gradingMode === 'graded' && (
              <BranchingCorrectsRow
                checked={branchingOn}
                onToggle={() => toggleBranching(!branchingOn)}
              />
            )}

            {/* The carousel encloses the TARGET and nothing else. Tolerance and Site Conditions stay
                outside it, unaffected by which branch is showing. */}
            {branchingOn && activeBranch ? (
              <BranchConditionCarousel
                seedVariable={(() => { const n = variableChipNames(questionHtmlRef.current); return n.length === 1 ? n[0] : undefined; })()}
                ref={branchPrevButtonRef}
                equation={conditionalEquation}
                onEquationChange={v => { setConditionalEquation(v); pushTargetChange({ conditionalEquation: v }); }}
                variables={variables}
                rolledValues={rolledValues}
                equationInvalid={gpsEquationInvalid}
                equationWorks={gpsEquationWorks}
                hasError={editorIssues.some(i => i.severity === 'error' && ['no-equation', 'invalid-equation', 'coverage-gap'].includes(i.key))}
                branches={conditionalBranches!}
                activeIndex={clampedBranchIndex}
                onActiveIndexChange={setActiveBranchIndex}
                onBranchChange={updateLocationBranch}
                onAdd={addLocationBranch}
                onRemove={removeLocationBranch}
                onFieldFocus={disconnectSharedToolbar}
              >
                {targetFieldsJsx}
              </BranchConditionCarousel>
            ) : (
              /* The scenario's own box — the carousel's shape with a variant in place of an
                 equation, and nothing at all when this block follows nothing. It encloses the
                 TARGET and nothing else, exactly as the carousel does: tolerance and site
                 conditions describe how precisely to answer, not where, so they stay outside. */
              <ScenarioSetupFrame
                scenario={scenario.activeScenario}
                name={(scenario.activeScenarioVersion as { name?: string } | null)?.name}
                pager={scenario.pager}
                unreviewedCount={scenario.unreviewedCount}
                onDismissUnreviewed={scenario.dismissUnreviewed}
              >
                {targetFieldsJsx}
              </ScenarioSetupFrame>
            )}
            <div className='grid grid-cols-2 gap-2'>
              <div className='flex flex-col'>
                <label htmlFor='gps-tolerance-radius' className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.gps.fieldToleranceRadius')}</label>
                <NumberField fieldId='toleranceRadius'
                  grammar='decimal'
                  value={toleranceRadius}
                  onChange={v => { setToleranceRadius(v); push({ toleranceRadius: v }); }}
                  hasError={toleranceInvalid}
                  ariaLabel={t('blocks.gps.fieldToleranceRadius')}
                  suffix={t('blocks.gps.unitMeters')}
                  maxLength={TOLERANCE_MAX_CHARS}
                  onFocus={disconnectSharedToolbar}
                />
              </div>
              <div className='flex flex-col'>
                <label htmlFor='gps-accuracy-cutoff' className='mb-1.5 text-sm font-medium text-ws-text'>{t('blocks.gps.fieldAccuracyCutoff')}</label>
                <SelectMenu fieldId='accuracyCutoff' fullWidth
                  value={accuracyCutoff}
                  onChange={(v: string) => { setAccuracyCutoff(v); push({ accuracyCutoff: v }); }}
                  options={GPS_ACCURACY_PRESETS.map(pr => ({
                    value: String(pr.meters),
                    label: t(`blocks.gps.${ACCURACY_PRESET_I18N_KEYS[pr.id]}`),
                  }))}
                  ariaLabel={t('blocks.gps.fieldAccuracyCutoff')}
                />
              </div>
            </div>

            {/* Extra separation from the fields above — capture/paste are alternate ways to fill
                them in, not a continuation of the same field group. */}
            <div className='mt-1 flex flex-col gap-3'>
              <div className='flex flex-col gap-1'>
                <Button onClick={handleCapture} disabled={isCapturing || !GEO_SUPPORTED}>
                  <Crosshair aria-hidden='true' className='h-4 w-4' />
                  {isCapturing ? t('blocks.gps.capturing') : t('blocks.gps.captureButton')}
                </Button>
                {/* "This browser has no Geolocation API" stays inline: it's a permanent property of
                    the device the teacher is authoring on, not a failed attempt, so a Toast that
                    auto-dismisses after 4s would be exactly the wrong shape for it. The failed
                    attempt itself (captureError) is a Toast — see the render tail. */}
                {!GEO_SUPPORTED && (
                  <p role='alert' className='flex items-center gap-1.5 text-xs text-ws-warning-fg'>
                    <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
                    {t('blocks.gps.requiresLocationServices')}
                  </p>
                )}
                <p className='text-xs italic text-ws-muted'>{t('blocks.gps.captureAccuracyNote')}</p>
                {/* Shaded + accent-bordered so the staged reading reads as its own "review this
                    before accepting" surface rather than more body content — it holds a value that
                    is NOT yet committed to the target fields. Turns warning-coloured when the
                    reading itself is suspect, so the box's own colour carries the verdict. */}
                {pendingCapture && (
                  <div className={`flex flex-col gap-2 rounded-lg border-2 px-3 py-2.5 ${
                    pendingCaptureAccuracyPoor || pendingCaptureLooksNetworkBased
                      ? 'border-ws-warning-fg bg-ws-warning-bg'
                      : 'border-ws-primary bg-ws-primary/5'
                  }`}>
                    <p className='text-center text-lg font-semibold text-ws-text'>{t('blocks.gps.pendingCaptureLat', { lat: fmtLocale(pendingCapture.lat, i18n.language) })}</p>
                    <p className='text-center text-lg font-semibold text-ws-text'>{t('blocks.gps.pendingCaptureLng', { lng: fmtLocale(pendingCapture.lng, i18n.language) })}</p>
                    <p className='text-center text-xs text-ws-muted'>{t('blocks.gps.pendingCaptureAccuracy', { accuracy: fmtLocale(pendingCapture.accuracy, i18n.language) })}</p>
                    {pendingCaptureAccuracyPoor && (
                      <p role='alert' className='flex items-center gap-1.5 text-xs text-ws-warning-fg'>
                        <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
                        {t('blocks.gps.pendingCaptureAccuracyWarning', { cutoff: fmtLocale(accuracyCutoffNum, i18n.language) })}
                      </p>
                    )}
                    {pendingCaptureLooksNetworkBased && (
                      <p role='alert' className='flex items-center gap-1.5 text-xs text-ws-warning-fg'>
                        <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
                        {t('blocks.gps.pendingCaptureNetworkBasedWarning')}
                      </p>
                    )}
                    <div className='flex justify-end'>
                      <Button onClick={handleUsePendingCapture} className='shrink-0'>
                        {t('blocks.gps.pendingCaptureUse')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className='flex flex-col gap-1'>
                <label htmlFor='gps-paste-input' className='text-sm font-medium text-ws-text'>{t('blocks.gps.pasteLabel')}</label>
                <div className='flex items-center gap-2'>
                  {/* Highlighted for as long as the content stays unusable, rather than raising a
                      transient message — recomputed from the field's own value on every keystroke,
                      so it appears the moment the text can't be parsed and clears itself the moment
                      it can. Blank is not invalid: an empty field is just empty. */}
                  <TextField
                    variant='prose'
                    value={pasteInput}
                    onChange={setPasteInput}
                    hasError={pasteInvalid}
                    ariaLabel={t('blocks.gps.pasteLabel')}
                    placeholder={t('blocks.gps.pastePlaceholder')}
                    onFocus={disconnectSharedToolbar}
                    className='min-w-0 flex-1' />
                  {/* Disabled rather than clickable-but-disabled: a button that visibly does nothing
                      when pressed reads as broken. Blank also disables it — there's nothing to
                      apply — so the only enabled state is "this will actually work". */}
                  <Button onClick={handleApplyPaste} disabled={pasteInvalid || pasteInput.trim() === ''} className='shrink-0'>
                    {t('blocks.gps.pasteApply')}
                  </Button>
                </div>
                <p className='text-xs italic text-ws-muted'>{t('blocks.gps.pasteHint')}</p>
              </div>
            </div>
          </>)}
          </DrivenRowFrame>
          </div>
      }
    />
    </>
  );
}
